import type { TravelNights } from './types';
import { keepPreviousData, useQuery, useMutation, useQueryClient, useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient, ApiError } from './client';
// The calendar surface owns the one module of zone-aware date primitives (see its header comment),
// and `useDayReservations` below has to name a calendar day in the tenant's zone. Giving this file
// its own copy of that arithmetic would recreate the duplication the whole phase exists to remove,
// so the leaf module is imported instead. It is pure, imports nothing itself, and pulls no page
// state into `src/api/vektra/` — deliberately the only exception to this directory's rule of
// depending on nothing under `pages/`.
import { isoDateInZone } from '../../pages/vektra/calendar/zone';
import type {
  EventFilterParams,
  PaymentDetailsDocument,
  PaymentDetailsOptions,
  PaymentDetailsProfile,
  PaymentDetailsSendResult,
  PaymentExport,
  PaymentLedgerColumnKey,
  PaymentLedgerFilters,
  PaymentLedgerRow,
  PaymentLedgerSort,
  PaymentRemindResult,
  SavePaymentDetailsProfileRequest,
  SendPaymentDetailsRequest,
  FileRemarkRequest,
  VehicleEvent,
  VehicleFuelEvent,
  RefuelVehicleRequest,
  PageResponse,
  VehicleResponse,
  VehicleLocationResponse,
  VehicleAvailabilityResponse,
  VehicleAvailabilitySlotsResponse,
  PeriodVehicleUtilisationResponse,
  DayVehicleReservationsResponse,
  CreateVehicleRequest,
  UpdateVehicleRequest,
  UpdateLocationRequest,
  WorkOrderResponse,
  CreateDraftWorkOrderRequest,
  ReserveVehiclesRequest,
  CreateWorkOrderRequest,
  AdvanceWorkOrderStepRequest,
  CreatePaymentRequest,
  UpdatePaymentRequest,
  CustomerResponse,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  MaintenanceResponse,
  MaintenanceListFilters,
  MaintenanceFleetVehicle,
  MaintenanceOverviewSummary,
  VehicleType,
  MaintenanceHistoryEntry,
  MaintenanceStatus,
  SaveMaintenanceChecklistRequest,
  ScheduleMaintenanceRequest,
  SetMaintenanceDueTargetRequest,
  UpdateMaintenanceWindowRequest,
  VehicleMaintenanceCycleStatus,
  CreateMaintenanceRequest,
  NotificationResponse,
  PushConfigResponse,
  VoiceTranscriptionConfigResponse,
  VoiceTranscriptionJobResponse,
  RegisterPushSubscriptionRequest,
  UserResponse,
  CreateUserRequest,
  UpdateUserRequest,
  FileUploadResponse,
  CreateReservationRequest,
  ReservationResponse,
  OnboardingStatusResponse,
  PortalIntegrationSummary,
  CustomerMessagesLanguageDTO,
  CustomerMessagesLanguageCode,
  WarehouseResponse,
  CreateWarehouseRequest,
  UpdateWarehouseRequest,
  ChangePasswordRequest,
  BusinessAccountResponse,
  UpdateBusinessAccountRequest,
  BillingConfigResponse,
  UpsertBillingConfigRequest,
  CostEstimateRequest,
  CostEstimateResponse,
  CustomerAddressResponse,
  CreateCustomerAddressRequest,
  UpdateCustomerAddressRequest,
  OperationalScheduleResponse,
  UpdateOperationalScheduleRequest,
  VehicleTypeOptionResponse,
  VehicleOptionFilterCriterion,
  VehicleOptionFilterRequest,
  CreateVehicleParameterRequest,
  UpdateVehicleParameterRequest,
  InvoiceResponse,
  AddInvoiceEntryRequest,
  UpdateInvoiceRequest,
  PaymentResponse,
  StatusHistoryEntryResponse,
  WorkflowStepResponse,
  VehicleWorkLogResponse,
  CreateMaintenanceWorkLogRequest,
  CreateWorkLogForOperatorRequest,
  RentalDay,
  UpdateRentalDaysRequest,
  CreateWorkLogBatchRequest,
  CreateTravelNightsRequest,
  CreateMyTravelNightsRequest,
  WorkLogExport,
  UnloggedRent,
  WorkLogOperator,
  RentalEntryOption,
  CreateVehicleWorkLogRequest,
  WorkLogDayResponse,
  RejectWorkLogRequest,
  CommissionOverrideRequest,
  WorkLogListFilters,
  WorkLogSummaryResponse,
  OperatorWorkMonthResponse,
  PayRates,
  PayRateVehicleModel,
  UpdatePayRatesRequest,
  CommissionReport,
  VehicleLabelDTO,
  VehicleTypeDTO,
  MaintenanceCycleConfigDTO,
  CreateMaintenanceCycleConfigRequest,
  UpdateMaintenanceCycleConfigRequest,
  VehicleInspectionTemplateDTO,
  ResolvedInspectionTemplateDTO,
  CreateVehicleInspectionTemplateRequest,
  UpdateVehicleInspectionTemplateRequest,
  SeedMaintenanceDefaultsRequest,
  MaintenanceSeedResultDTO,
  MaintenanceDefaultsAvailabilityDTO,
  SubmitWorkOrderInspectionRequest,
  WorkOrderInspectionDTO,
  UpdateMaintenanceRequest,
  ClearBrokenStatusRequest,
  InspectionTriggerStep,
  MaintenanceCycleCode,
  PreDepartureCheckDTO,
  SubmitPreDepartureCheckRequest,
  WorkOrderFileResponse,
  WorkOrderFilePurpose,
  SensorAssignmentResponse,
  SensorReadingResponse,
  SensorResponse,
  SensorStatus,
  SensorType,
  AssignSensorRequest,
  CreateSensorRequest,
  CompletionReportResponse,
  CompletionReportShareTokenResponse,
  EstimateConversionPreview,
  EstimateDocument,
  EstimateShareTokenResponse,
  EmailedReportResponse,
  EmailEstimateRequest,
  WorkOrderStatusOption,
  FinancialPeriod,
  FinancialComparison,
  OperationalSummaryResponse,
  AgendaDay,
  HomeAgendaResponse,
  FinancialSummaryResponse,
  WorkOrderSummaryV2,
  WorkOrderDetailV2,
  WorkOrderItemV2,
  WorkOrderTaskV2,
  WorkOrderCreateRequest,
  WorkOrderUpdateRequest,
  WorkOrderItemDraftRequest,
  PatchWorkOrderItemControlsRequest,
  WorkOrderSubmitRequest,
  CancelRequest,
  ItemCancelRequest,
  FleetManagerAssignRequest,
  PaymentGate,
  GateReviewRequest,
  CloseWorkOrderRequest,
  RecordPaymentCollectionRequest,
  CancelPaymentRequest,
  SplitPaymentRequest,
  OperatorAssignmentRequest,
  BulkOperatorAssignmentRequest,
  OperatorCandidatesResponse,
  OperatorConfirmationRequest,
  PreRentCheckInRequest,
  TransitionNoteRequest,
  RentStartRequest,
  ReturnStartRequest,
  ReturnCheckInRequest,
  ReplaceVehicleRequest,
  DirectExtensionRequest,
  RentalExtensionRequest,
  RentalExtensionDecision,
  RentalExtensionRejection,
  DamageReviewRequest,
  PaymentCollectionRequest,
  CashNotPaidRequest,
  ConflictResolutionRequest,
  StatusHistoryEntry,
  ConflictAlert,
  WorkOrderStatusV2,
  WorkOrderTypeV2,
  WorkOrderOrigin,
  BlockerType,
  WorkOrderItemStatusV2,
  ConflictAlertStatus,
  ConflictAlertType,
  BuildInfoResponse,
  CustomerProfileChange,
  CustomerProfileLinkIssued,
  PublicCustomerProfile,
  SubmitCustomerProfileRequest,
  DemoRequestRequest,
  DemoRequestResponse,
  TelegramConnectLink,
  CustomerTelegramStatus,
  TelegramConnectCode,
  StaffTelegramStatus,
  SmsMessage,
  LiveProfileLink,
  SmsGatewayStatus,
  SupportRequestResponse,
  SupportRequestFileResponse,
  CreateSupportRequestRequest,
  KharkivHouseResponse,
  KharkivStreetResponse,
  EstimateSendOptions,
  EstimateSendResult,
  SendEstimateRequest,
  EstimateShortLink
} from './types';

// ──────────────────────────────────────────────
// Vehicle hooks
// ──────────────────────────────────────────────

export function useVehicles(page = 0, size = 20, search?: string, groupType?: string, enabled = true) {
  return useQuery({
    enabled,
    queryKey: ['vehicles', page, size, search, groupType],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      if (search) params.append('search', search);
      if (groupType) params.append('groupType', groupType);
      return apiClient.get<PageResponse<VehicleResponse>>(`/vehicles?${params}`);
    }
  });
}

// VPARAM-03 — the server localizes these labels from Accept-Language, so the cached answer belongs to the
// language it was fetched in; keying by language is what makes a language switch show the new names.
export function useVehicleGroupTypes() {
  const { i18n } = useTranslation();
  return useQuery({
    queryKey: ['vehicles', 'group-types', i18n.language],
    queryFn: () => apiClient.get<VehicleLabelDTO[]>('/vehicles/group-types')
  });
}

export function useVehicleTypes() {
  const { i18n } = useTranslation();
  return useQuery({
    queryKey: ['vehicles', 'types', i18n.language],
    queryFn: () => apiClient.get<VehicleLabelDTO[]>('/vehicles/types')
  });
}

/**
 * The full catalogue of vehicle types, each carrying the group it belongs to.
 *
 * Typed `VehicleTypeDTO[]` rather than `VehicleLabelDTO[]`: the server has always sent `groupType`
 * and `groupTypeLabel` on this endpoint (`vektra-api/contracts/openapi.json`), and the narrower
 * declaration hid them from every caller. Widening is additive — a reader that only wanted
 * `code`/`label` still gets them.
 */
export function useAllVehicleTypes() {
  const { i18n } = useTranslation();
  return useQuery({
    queryKey: ['vehicles', 'types', 'all', i18n.language],
    queryFn: () => apiClient.get<VehicleTypeDTO[]>('/vehicles/types/all'),
    staleTime: 60 * 60 * 1000
  });
}

/** VPARAM-01 — the account's enabled parameters for a type, in catalog order (vehicle form, Fleet filters). */
export function useVehicleTypeOptions(vehicleType: string) {
  return useQuery({
    queryKey: ['vehicle-parameters', vehicleType, 'enabled'],
    queryFn: () => apiClient.get<VehicleTypeOptionResponse[]>(`/vehicles/types/${vehicleType}/options`),
    enabled: !!vehicleType
  });
}

/** VPARAM-01 — every parameter of the account's catalog for a type, disabled ones included. */
export function useVehicleParameters(vehicleType: string) {
  return useQuery({
    queryKey: ['vehicle-parameters', vehicleType, 'all'],
    queryFn: () => apiClient.get<VehicleTypeOptionResponse[]>(`/vehicle-parameters?vehicleType=${vehicleType}`),
    enabled: !!vehicleType
  });
}

export function useCreateVehicleParameter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateVehicleParameterRequest) => apiClient.post<VehicleTypeOptionResponse>('/vehicle-parameters', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-parameters'] });
    }
  });
}

export function useUpdateVehicleParameter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateVehicleParameterRequest }) =>
      apiClient.put<VehicleTypeOptionResponse>(`/vehicle-parameters/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-parameters'] });
    }
  });
}

export function useVehicle(id: string) {
  return useQuery({
    queryKey: ['vehicles', id],
    queryFn: () => apiClient.get<VehicleResponse>(`/vehicles/${id}`),
    enabled: !!id
  });
}

export function useVehicleAvailability(
  workOrderId: string,
  vehicleType: string = 'MOBILE_CRANE',
  rentStartTime?: string,
  rentEndTime?: string,
  enabled = true
) {
  return useQuery({
    queryKey: ['vehicles', 'availability', workOrderId, vehicleType, rentStartTime, rentEndTime],
    queryFn: () => {
      const params = new URLSearchParams({ vehicleType, workOrderId });
      if (rentStartTime) params.append('rentStartTime', rentStartTime);
      if (rentEndTime) params.append('rentEndTime', rentEndTime);
      return apiClient.get<VehicleAvailabilityResponse[]>(`/vehicles/availability?${params}`);
    },
    enabled: enabled && !!workOrderId
  });
}

/**
 * US-03 — the candidate list: every rentable machine of one type, answered for one window and one
 * drop-off point. `GET /vehicles/availability/candidates` takes no `workOrderId`, because nothing
 * has been written to an order yet when this is asked.
 *
 * Two divergences from `useVehicleAvailability` above are load-bearing, not cosmetic:
 *
 *   - The key is `['vehicles', 'candidates', …]`, never under the `['vehicles','availability']`
 *     prefix that the create page's 600 ms autosave blanket-invalidates — otherwise the list
 *     refetches while the user types in the notes field.
 *   - `staleTime: 0`. Availability is never served from cache; a cached answer is a machine
 *     someone else may already hold.
 *
 * `vehicleType` has no default. The old `'MOBILE_CRANE'` fallback on the availability hook is the
 * gap S1 defect, and a default here would put it back.
 *
 * VPARAM-02 — `filters`, trailing and optional, narrows the question by parameter ranges: each
 * criterion travels as one repeated `filter=KEY:min:max` query parameter (an open bound is empty)
 * and sits in the key, so two differently filtered questions are two cache entries. Callers that
 * pass nothing ask exactly what they asked before.
 */
export function useVehicleCandidates(
  dropOffLat: number | null | undefined,
  dropOffLng: number | null | undefined,
  vehicleType: string,
  rentStartTime?: string,
  rentEndTime?: string,
  enabled = true,
  filters?: VehicleOptionFilterCriterion[]
) {
  const filterTokens = (filters ?? []).map((c) => `${c.key}:${c.min ?? ''}:${c.max ?? ''}`);
  return useQuery({
    queryKey: ['vehicles', 'candidates', dropOffLat, dropOffLng, vehicleType, rentStartTime, rentEndTime, filterTokens],
    queryFn: () => {
      const params = new URLSearchParams({
        dropOffLng: String(dropOffLng),
        dropOffLat: String(dropOffLat),
        vehicleType,
        rentStartTime: rentStartTime ?? '',
        rentEndTime: rentEndTime ?? ''
      });
      for (const token of filterTokens) params.append('filter', token);
      return apiClient.get<VehicleAvailabilityResponse[]>(`/vehicles/availability/candidates?${params}`);
    },
    enabled: enabled && dropOffLat != null && dropOffLng != null && !!vehicleType && !!rentStartTime && !!rentEndTime,
    staleTime: 0
  });
}

export function useVehicleLineAvailabilities(
  lines: Array<{
    key: string;
    workOrderId: string;
    vehicleType: string;
    rentStartTime?: string;
    rentEndTime?: string;
    enabled?: boolean;
  }>
) {
  return useQueries({
    queries: lines.map((line) => ({
      queryKey: ['vehicles', 'availability', line.workOrderId, line.vehicleType, line.rentStartTime, line.rentEndTime, line.key],
      queryFn: () => {
        const params = new URLSearchParams({ vehicleType: line.vehicleType, workOrderId: line.workOrderId });
        if (line.rentStartTime) params.append('rentStartTime', line.rentStartTime);
        if (line.rentEndTime) params.append('rentEndTime', line.rentEndTime);
        return apiClient.get<VehicleAvailabilityResponse[]>(`/vehicles/availability?${params}`);
      },
      enabled: Boolean(line.enabled && line.workOrderId && line.vehicleType && line.rentStartTime && line.rentEndTime)
    }))
  });
}

export function useVehicleAvailabilitySlots(vehicleId: string, workOrderId: string, enabled = true) {
  return useQuery({
    queryKey: ['vehicles', 'availability', vehicleId, 'slots', workOrderId],
    queryFn: () => {
      const params = new URLSearchParams({ workOrderId });
      return apiClient.get<VehicleAvailabilitySlotsResponse>(`/vehicles/availability/${vehicleId}/slots?${params}`);
    },
    enabled: enabled && !!vehicleId && !!workOrderId
  });
}

export const GROUP_TO_VEHICLE_TYPES: Record<string, string[]> = {
  EARTHMOVING_AND_SITE_PREPARATION: [
    'EXCAVATOR',
    'MINI_EXCAVATOR',
    'CRAWLER',
    'WHEELED_EXCAVATOR',
    'BULLDOZER',
    'BACKHOE_LOADER',
    'SKID_STEER_LOADER',
    'COMPACT_TRACK_LOADER',
    'GRADER',
    'TRENCHER',
    'LAND_CLEARING_MACHINE'
  ],
  MATERIAL_TRANSPORT: ['DUMP_TRUCK', 'FLATBED_TRUCK', 'TIPPER_TRUCK', 'PICKUP_TRUCK', 'TRAILER', 'TANKER', 'WATER_TANKER'],
  LIFTING_AND_HOISTING: [
    'MOBILE_CRANE',
    'TOWER_CRANE',
    'MINI_CRANE',
    'TELESCOPIC_HANDLER',
    'FORKLIFT',
    'BOOM_LIFT',
    'SCISSOR_LIFT',
    'MATERIAL_HOIST'
  ],
  CONCRETE_AND_MASONRY: ['CONCRETE_MIXER_TRUCK', 'CONCRETE_PUMP_TRUCK', 'MINI_CONCRETE_MIXER', 'MORTAR_MIXER', 'CONCRETE_VIBRATOR_TRUCK'],
  COMPACTION_AND_PAVING: ['PLATE_COMPACTOR', 'ROLLER_SINGLE_DRUM', 'ROLLER_TANDEM', 'RAMMER_JUMPING_JACK', 'ASPHALT_PAVER'],
  SPECIALIZED: ['PILE_DRIVER', 'DRILLING_RIG', 'GENERATOR_TRUCK', 'CRANE_MOUNTED_AUGER', 'VACUUM_EXCAVATOR'],
  SUPPORT_VEHICLES: ['MAINTENANCE_VAN', 'TOOL_TRANSPORT_VEHICLE', 'FUEL_TRUCK', 'PORTABLE_TOILET_TRUCK', 'SKIP_LOADER']
};

/**
 * Fetches vehicle availability for every vehicleType within the specified vehicleGroupType,
 * running one query per type in parallel, then merges results (de-duped by vehicle id).
 */
export function useVehicleGroupAvailability(workOrderId: string, vehicleGroupType?: string, enabled = true) {
  const vehicleTypes = vehicleGroupType ? (GROUP_TO_VEHICLE_TYPES[vehicleGroupType] ?? []) : [];
  const results = useQueries({
    queries: vehicleTypes.map((vehicleType) => ({
      queryKey: ['vehicles', 'availability', workOrderId, vehicleType],
      queryFn: () => {
        const params = new URLSearchParams({ vehicleType, workOrderId });
        return apiClient.get<VehicleAvailabilityResponse[]>(`/vehicles/availability?${params}`);
      },
      enabled: enabled && !!workOrderId && vehicleTypes.length > 0
    }))
  });

  const isLoading = results.some((r) => r.isLoading);
  const seen = new Set<string>();
  const data: VehicleAvailabilityResponse[] = [];
  for (const r of results) {
    for (const v of r.data ?? []) {
      if (!seen.has(v.id)) {
        seen.add(v.id);
        data.push(v);
      }
    }
  }

  return { data, isLoading };
}

export function useCalendarGroupTypes() {
  return useQuery({
    queryKey: ['calendar', 'group-types'],
    queryFn: () => apiClient.get<string[]>('/calendar/group-types'),
    staleTime: 10 * 60 * 1000 // 10 min — group types change only when vehicles are added/removed
  });
}

export function usePeriodReservations(periodStart?: Date, periodEnd?: Date, vehicleGroupType?: string, enabled: boolean = true) {
  return useQuery({
    // The caller gates this on having a tenant zone to build the range in. Without the gate the
    // query would fire range-less, the server would answer for its own default week, and the grid
    // would paint that range for a frame before the requested one replaced it.
    enabled,
    queryKey: ['vehicles', 'availability', 'period-reservations', periodStart?.toISOString(), periodEnd?.toISOString(), vehicleGroupType],
    queryFn: () => {
      const params = new URLSearchParams();
      if (periodStart) params.append('periodStart', periodStart.toISOString());
      if (periodEnd) params.append('periodEnd', periodEnd.toISOString());
      if (vehicleGroupType) params.append('vehicleGroupType', vehicleGroupType);
      return apiClient.get<PeriodVehicleUtilisationResponse[]>(`/vehicles/availability/period-reservations?${params}`);
    },
    staleTime: 5 * 60 * 1000
  });
}

/**
 * Per-vehicle reservation slots overlapping a single calendar day in the tenant's operational time
 * zone. Powers the calendar's Day Timeline. The week-aggregate `usePeriodReservations` is a separate
 * query.
 */
export function useDayReservations(date?: Date, timeZone?: string, vehicleGroupType?: string, enabled: boolean = true) {
  // The `date` parameter names a calendar day, and which day an instant falls on is a question only
  // a zone can answer. Two answers have been wrong here before: a zero-offset day (`toISOString()`),
  // and then the browser's own day — whichever one the operator's laptop happens to be having. The
  // server buckets this day's boundaries in the tenant's operational zone, so the client has to ask
  // in that same zone or the two disagree about which reservations fall on the day. With no zone in
  // hand there is no day to ask for, and the query does not run rather than guessing one.
  const isoDate = date && timeZone ? isoDateInZone(date, timeZone) : undefined;
  return useQuery({
    enabled: enabled && !!isoDate,
    queryKey: ['vehicles', 'availability', 'day-reservations', isoDate, timeZone, vehicleGroupType],
    queryFn: () => {
      const params = new URLSearchParams();
      if (isoDate) params.append('date', isoDate);
      if (vehicleGroupType) params.append('vehicleGroupType', vehicleGroupType);
      return apiClient.get<DayVehicleReservationsResponse[]>(`/vehicles/availability/day-reservations?${params}`);
    },
    staleTime: 60 * 1000
  });
}

/**
 * VPARAM-01 — vehicles of one type narrowed by search text and parameter ranges. A query, not a
 * mutation: the Fleet page lists through it while a type is selected. Disabled without a type.
 */
export function useFilterVehicles(page: number, size: number, request: VehicleOptionFilterRequest | null) {
  return useQuery({
    queryKey: ['vehicles', 'filter', page, size, request],
    queryFn: () =>
      apiClient.post<PageResponse<VehicleResponse>>(`/vehicles/filter?page=${page}&size=${size}`, request as VehicleOptionFilterRequest),
    enabled: !!request?.vehicleType
  });
}

export function useCreateVehicle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateVehicleRequest) => apiClient.post<VehicleResponse>('/vehicles', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    }
  });
}

export function useUpdateVehicle(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateVehicleRequest) => apiClient.put<VehicleResponse>(`/vehicles/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', id] });
    }
  });
}

export function useUpdateVehicleLocation(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateLocationRequest) => apiClient.post<VehicleLocationResponse>(`/vehicles/${id}/location`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', id] });
    }
  });
}

// ──────────────────────────────────────────────
// Work Order hooks
// ──────────────────────────────────────────────

export function useWorkOrders(
  page = 0,
  size = 20,
  status?: string | string[],
  includeArchived?: boolean,
  assignedToMe?: boolean,
  sort?: string,
  createdFrom?: string,
  createdTo?: string,
  rentFrom?: string,
  rentTo?: string
) {
  const statusKey = Array.isArray(status) ? [...status].sort().join(',') : status;
  return useQuery({
    queryKey: ['work-orders', page, size, statusKey, includeArchived, assignedToMe, sort, createdFrom, createdTo, rentFrom, rentTo],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      if (Array.isArray(status)) {
        status.forEach((s) => params.append('status', s));
      } else if (status) {
        params.append('status', status);
      }
      if (includeArchived) params.append('includeArchived', 'true');
      if (assignedToMe) params.append('assignedToMe', 'true');
      if (sort) params.append('sort', sort);
      // DASH-06 -- inclusive UTC calendar dates; the dashboard's period window.
      if (createdFrom) params.append('createdFrom', createdFrom);
      if (createdTo) params.append('createdTo', createdTo);
      // MAP-02 -- inclusive UTC calendar dates; orders with a non-cancelled entry whose planned
      // rental window overlaps them (VEK-171).
      if (rentFrom) params.append('rentFrom', rentFrom);
      if (rentTo) params.append('rentTo', rentTo);
      return apiClient.get<PageResponse<WorkOrderResponse>>(`/work-orders?${params}`);
    }
  });
}

export function useWorkOrderStatuses() {
  return useQuery({
    queryKey: ['work-orders', 'statuses', apiClient.getLanguage()],
    queryFn: () => apiClient.get<WorkOrderStatusOption[]>('/work-orders/statuses'),
    staleTime: Infinity,
    enabled: !!apiClient.getToken()
  });
}

export function usePaymentTypes() {
  return useQuery({
    queryKey: ['work-orders', 'payment-types'],
    queryFn: () => apiClient.get<string[]>('/work-orders/payment-types'),
    staleTime: Infinity,
    enabled: !!apiClient.getToken()
  });
}

export function useWorkOrder(id: string) {
  return useQuery({
    queryKey: ['work-orders', id],
    queryFn: () => apiClient.get<WorkOrderResponse>(`/work-orders/${id}`),
    enabled: !!id
  });
}

export function useWorkOrderWorkflowSteps(id: string, enabled = true) {
  return useQuery({
    queryKey: ['work-orders', id, 'workflow-steps'],
    queryFn: () => apiClient.get<WorkflowStepResponse[]>(`/work-orders/${id}/workflow-steps`),
    enabled: enabled && !!id
  });
}

export function useWorkOrderHistory(id: string, enabled = true) {
  return useQuery({
    queryKey: ['work-orders', id, 'history'],
    queryFn: () => apiClient.get<PageResponse<StatusHistoryEntryResponse>>(`/work-orders/${id}/history?size=100`),
    enabled: enabled && !!id
  });
}

export function useWorkOrderWorkLogs(id: string, enabled = true) {
  return useQuery({
    queryKey: ['work-orders', id, 'work-logs'],
    queryFn: () => apiClient.get<VehicleWorkLogResponse[]>(`/work-orders/${id}/work-logs`),
    enabled: enabled && !!id
  });
}

export function useVehicleWorkLogs(
  vehicleId: string,
  filters?: { from?: string; to?: string; source?: string; approved?: boolean },
  enabled = true
) {
  return useQuery({
    queryKey: ['vehicles', vehicleId, 'work-logs', filters],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters?.from) params.append('from', filters.from);
      if (filters?.to) params.append('to', filters.to);
      if (filters?.source) params.append('source', filters.source);
      if (filters?.approved != null) params.append('approved', String(filters.approved));
      const suffix = params.toString() ? `?${params}` : '';
      return apiClient.get<VehicleWorkLogResponse[]>(`/vehicles/${vehicleId}/work-logs${suffix}`);
    },
    enabled: enabled && !!vehicleId
  });
}

/** WORKLOG-01 — the entry's calendar dates with the row that covers each; empty before the rent starts. */
export function useWorkLogDays(workOrderId: string, itemId: string, enabled = true) {
  return useQuery({
    queryKey: ['work-orders', workOrderId, 'items', itemId, 'work-log-days'],
    queryFn: () => apiClient.get<WorkLogDayResponse[]>(`/work-orders/${workOrderId}/items/${itemId}/work-log-days`),
    enabled: enabled && !!workOrderId && !!itemId
  });
}

export function useCreateWorkLog(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateVehicleWorkLogRequest) =>
      apiClient.post<VehicleWorkLogResponse>(`/work-orders/${workOrderId}/work-logs`, data),
    onSuccess: (log) => {
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId, 'work-logs'] });
      if (log?.workOrderItemId) {
        queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId, 'items', log.workOrderItemId, 'work-log-days'] });
      }
      if (log?.vehicleId) queryClient.invalidateQueries({ queryKey: ['vehicles', log.vehicleId, 'work-logs'] });
    }
  });
}

/** WORKLOG-03 / WORKLOG-07 — the reviewer list's filters as query params, shared by the list and its summary. */
function workLogFilterParams(filters: WorkLogListFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.operatorId) params.append('operatorId', filters.operatorId);
  if (filters.from) params.append('from', filters.from);
  if (filters.to) params.append('to', filters.to);
  if (filters.activityType) params.append('activityType', filters.activityType);
  return params;
}

/** WORKLOG-03 — the account's work logs, newest business date first; the four reviewing roles only. */
export function useWorkLogs(filters: WorkLogListFilters, page = 0, size = 20, enabled = true) {
  return useQuery({
    queryKey: ['work-logs', filters, page, size],
    queryFn: () => {
      const params = workLogFilterParams(filters);
      params.set('page', String(page));
      params.set('size', String(size));
      return apiClient.get<PageResponse<VehicleWorkLogResponse>>(`/work-logs?${params}`);
    },
    placeholderData: (prev) => prev,
    enabled
  });
}

/**
 * WORKLOG-07 — totals of every row `useWorkLogs` admits under the same filters. Keyed under `['work-logs']`, so approve,
 * reject and override refresh it with the list.
 */
export function useWorkLogSummary(filters: WorkLogListFilters, enabled = true) {
  return useQuery({
    queryKey: ['work-logs', 'summary', filters],
    queryFn: () => apiClient.get<WorkLogSummaryResponse>(`/work-logs/summary?${workLogFilterParams(filters)}`),
    placeholderData: (prev) => prev,
    enabled
  });
}

/**
 * WORKLOG-08 (VEK-159) — one operator's month of work hours (`month` absent: the account's current month). Keyed under
 * `['work-logs']`, so approve / reject / override invalidations refresh it too.
 */
export function useOperatorWorkMonth(operatorId: string | undefined, month: string | undefined) {
  return useQuery({
    queryKey: ['work-logs', 'operator-month', operatorId, month ?? null],
    queryFn: () =>
      apiClient.get<OperatorWorkMonthResponse>(
        `/work-logs/operators/${encodeURIComponent(operatorId ?? '')}/month${month ? `?month=${encodeURIComponent(month)}` : ''}`
      ),
    placeholderData: (prev) => prev,
    enabled: !!operatorId
  });
}

/** WORKLOG-04 — the caller's own work logs (`FLEET_OPERATOR` only), filtered as the reviewer list is. */
export function useMyWorkLogs(filters: Omit<WorkLogListFilters, 'operatorId'>, page = 0, size = 20, enabled = true) {
  return useQuery({
    queryKey: ['work-logs', 'mine', filters, page, size],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      if (filters.status) params.append('status', filters.status);
      if (filters.from) params.append('from', filters.from);
      if (filters.to) params.append('to', filters.to);
      if (filters.activityType) params.append('activityType', filters.activityType);
      return apiClient.get<PageResponse<VehicleWorkLogResponse>>(`/work-logs/mine?${params}`);
    },
    placeholderData: (prev) => prev,
    enabled
  });
}

/** WORKLOG-12 (VEK-176) — the caller's running rents with days still to log, for the Rental option of *Log work hours*. */
export function useMyUnloggedRents(enabled = true) {
  return useQuery({
    queryKey: ['work-logs', 'mine', 'rents'],
    queryFn: () => apiClient.get<UnloggedRent[]>('/work-logs/mine/rents'),
    enabled
  });
}

/** WORKLOG-12 (VEK-176) — the caller's entries a night stay may be filed on this month. */
/** WORKLOG-17 (VEK-238) — the Night stay option's cards; replaces `useMyTravelEntries` (`GET /work-logs/mine/travel-entries`). */
export function useMyTravelNights(enabled = true) {
  return useQuery({
    queryKey: ['work-logs', 'mine', 'travel-nights'],
    queryFn: () => apiClient.get<TravelNights>('/work-logs/mine/travel-nights'),
    enabled
  });
}

/** WORKLOG-12 (VEK-176) — the caller's own past nights on one of their entries, one pending row per night. */
export function useCreateMyTravelNights() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateMyTravelNightsRequest) => apiClient.post<VehicleWorkLogResponse[]>('/work-logs/mine/travel', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-logs'] });
      queryClient.invalidateQueries({ queryKey: ['commissions'] });
    }
  });
}

/** WORKLOG-12 (VEK-176) — the caller's own list under the filters as an xlsx, every matching row. */
export function useExportMyWorkLogs() {
  return useMutation({
    mutationFn: (filters: Omit<WorkLogListFilters, 'operatorId'>) => {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.from) params.append('from', filters.from);
      if (filters.to) params.append('to', filters.to);
      if (filters.activityType) params.append('activityType', filters.activityType);
      return apiClient.get<WorkLogExport>(`/work-logs/mine/export?${params}`);
    }
  });
}

/** WORKLOG-12 (VEK-176) — the account's list under the filters as an xlsx, every matching row, with the operator. */
export function useExportWorkLogs() {
  return useMutation({
    mutationFn: (filters: WorkLogListFilters) => apiClient.get<WorkLogExport>(`/work-logs/export?${workLogFilterParams(filters)}`)
  });
}

/** COMM-08 (VEK-176) — the caller's month of income as an xlsx, one row per date and a total row. */
export function useExportMyIncome() {
  return useMutation({
    mutationFn: (month?: string) => apiClient.get<WorkLogExport>(`/work-logs/commissions/me/export${month ? `?month=${month}` : ''}`)
  });
}

/** WORKLOG-04 — maintenance hours on a vehicle outside any rent. */
export function useCreateMaintenanceWorkLog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateMaintenanceWorkLogRequest) => apiClient.post<VehicleWorkLogResponse>('/work-logs', data),
    onSuccess: (log) => {
      queryClient.invalidateQueries({ queryKey: ['work-logs'] });
      if (log?.vehicleId) queryClient.invalidateQueries({ queryKey: ['vehicles', log.vehicleId, 'work-logs'] });
    }
  });
}

/** WORKLOG-06 (VEK-157) — a reviewer files a missed day for an operator; the row comes back approved and priced. */
export function useCreateWorkLogForOperator() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWorkLogForOperatorRequest) => apiClient.post<VehicleWorkLogResponse>('/work-logs/for-operator', data),
    onSuccess: (log) => invalidateReviewedLog(queryClient, log)
  });
}

/**
 * PRICE-12 (VEK-177) — move the first rental day's start and/or the last day's end. The order read carries the days and
 * the price that follows them, so it is refetched; the commission estimate reads the days too.
 */
export function useUpdateRentalDays(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateRentalDaysRequest) =>
      apiClient.patch<RentalDay[]>(`/work-orders/${workOrderId}/items/${itemId}/rental-days`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId] });
    }
  });
}

/** WORKLOG-06 — the account's fleet operators a day may be filed for. */
export function useWorkLogOperators(enabled = true) {
  return useQuery({
    queryKey: ['work-logs', 'operators'],
    queryFn: () => apiClient.get<WorkLogOperator[]>('/work-logs/operators'),
    enabled
  });
}

/** WORKLOG-10 (VEK-164) — the operator's rents with days still to log, newest first, for the Rental tab of *Log time*. */
export function useUnloggedRents(operatorId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['work-logs', 'unlogged-rents', operatorId],
    queryFn: () => apiClient.get<UnloggedRent[]>(`/work-logs/unlogged-rents?operatorId=${encodeURIComponent(operatorId ?? '')}`),
    enabled: enabled && !!operatorId
  });
}

/** WORKLOG-10 (VEK-164) — every unlogged day of the chosen rents, filed approved in one act. */
export function useCreateWorkLogsForOperatorBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWorkLogBatchRequest) => apiClient.post<VehicleWorkLogResponse[]>('/work-logs/for-operator/batch', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-logs'] });
    }
  });
}

/** COMM-06 (VEK-165) — the operator's entries a night stay may be filed on (rent started, meeting the window). */
export function useTravelEntries(operatorId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['work-logs', 'travel-entries', operatorId],
    queryFn: () => apiClient.get<RentalEntryOption[]>(`/work-logs/travel-entries?operatorId=${encodeURIComponent(operatorId ?? '')}`),
    enabled: enabled && !!operatorId
  });
}

/** COMM-06 (VEK-165) — the operator's past nights on one entry, one approved row per night. */
export function useCreateTravelNights() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTravelNightsRequest) => apiClient.post<VehicleWorkLogResponse[]>('/work-logs/for-operator/travel', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-logs'] });
    }
  });
}

/** WORKLOG-06 — the entries an operator's rental day on `businessDate` may be filed against. */
export function useRentalEntries(operatorId: string | undefined, businessDate: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['work-logs', 'rental-entries', operatorId, businessDate],
    queryFn: () =>
      apiClient.get<RentalEntryOption[]>(
        `/work-logs/rental-entries?operatorId=${encodeURIComponent(operatorId ?? '')}&businessDate=${businessDate ?? ''}`
      ),
    enabled: enabled && !!operatorId && !!businessDate
  });
}

/** COMM-02 (VEK-147) — the account's operator pay rates. */
export function usePayRates(enabled = true) {
  return useQuery({
    queryKey: ['pay-rates'],
    queryFn: () => apiClient.get<PayRates>('/pay-rates'),
    enabled
  });
}

/** COMM-03 — the account's distinct vehicle models a rental override can name. */
export function usePayRateVehicleModels(enabled = true) {
  return useQuery({
    queryKey: ['pay-rates', 'vehicle-models'],
    queryFn: () => apiClient.get<PayRateVehicleModel[]>('/pay-rates/vehicle-models'),
    enabled
  });
}

/** COMM-02 — replaces the whole set; every commission read re-prices. */
export function useUpdatePayRates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdatePayRatesRequest) => apiClient.put<PayRates>('/pay-rates', data),
    onSuccess: (rates) => {
      queryClient.setQueryData(['pay-rates'], rates);
      queryClient.invalidateQueries({ queryKey: ['work-logs', 'commissions'] });
      // ONBOARD-02 — the rental default completes (or, cleared, re-opens) the pay-rates onboarding step.
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    }
  });
}

/** COMM-02 — the account's commission by employee and date for `from`..`to` (`YYYY-MM-DD`); the four reviewing roles. */
export function useCommissions(from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: ['work-logs', 'commissions', from, to],
    queryFn: () => apiClient.get<CommissionReport>(`/work-logs/commissions?${new URLSearchParams({ from, to })}`),
    placeholderData: (prev) => prev,
    enabled: enabled && !!from && !!to && from <= to
  });
}

/** COMM-02 — the caller's own month (`YYYY-MM`; the current month when absent); `FLEET_OPERATOR` only. */
export function useMyCommission(month?: string, enabled = true) {
  return useQuery({
    queryKey: ['work-logs', 'commissions', 'me', month ?? 'current'],
    queryFn: () => apiClient.get<CommissionReport>(`/work-logs/commissions/me${month ? `?month=${month}` : ''}`),
    placeholderData: (prev) => prev,
    enabled
  });
}

/** COMM-02 — the caller's own hours on one entry they are assigned to; `FLEET_OPERATOR` only. */
export function useEntryCommission(workOrderId: string, itemId: string, enabled = true) {
  return useQuery({
    queryKey: ['work-logs', 'commissions', 'entry', workOrderId, itemId],
    queryFn: () => apiClient.get<CommissionReport>(`/work-orders/${workOrderId}/items/${itemId}/commission`),
    enabled: enabled && !!workOrderId && !!itemId
  });
}

/** Every read a review changes: the account list, the order's logs, the entry's day list and the vehicle's logs. */
function invalidateReviewedLog(queryClient: ReturnType<typeof useQueryClient>, log: VehicleWorkLogResponse) {
  queryClient.invalidateQueries({ queryKey: ['work-logs'] });
  // WORKLOG-04 — a maintenance log has no order to refresh.
  if (log.workOrderId) {
    queryClient.invalidateQueries({ queryKey: ['work-orders', log.workOrderId, 'work-logs'] });
    queryClient.invalidateQueries({ queryKey: ['work-orders-v2', log.workOrderId, 'history'] });
  }
  if (log.workOrderId && log.workOrderItemId) {
    queryClient.invalidateQueries({ queryKey: ['work-orders', log.workOrderId, 'items', log.workOrderItemId, 'work-log-days'] });
  }
  if (log.vehicleId) queryClient.invalidateQueries({ queryKey: ['vehicles', log.vehicleId, 'work-logs'] });
}

export function useApproveWorkLog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.post<VehicleWorkLogResponse>(`/work-logs/${id}/approve`, {}),
    onSuccess: (log) => invalidateReviewedLog(queryClient, log)
  });
}

/** WORKLOG-03 — a pending work log refused with a reason; the operator files the date again. */
export function useRejectWorkLog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: RejectWorkLogRequest & { id: string }) =>
      apiClient.post<VehicleWorkLogResponse>(`/work-logs/${id}/reject`, body),
    onSuccess: (log) => invalidateReviewedLog(queryClient, log)
  });
}

/** COMM-04 — the account owner sets an approved row's commission with a reason; ACCOUNT_MANAGER, ADMIN, SUPER_ADMIN. */
export function useSetCommissionOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: CommissionOverrideRequest & { id: string }) =>
      apiClient.put<VehicleWorkLogResponse>(`/work-logs/${id}/commission-override`, body),
    onSuccess: (log) => invalidateReviewedLog(queryClient, log)
  });
}

/** COMM-04 — the row prices at hours × rate again. */
export function useClearCommissionOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<VehicleWorkLogResponse>(`/work-logs/${id}/commission-override`),
    onSuccess: (log) => invalidateReviewedLog(queryClient, log)
  });
}

export function useCreateDraftWorkOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateDraftWorkOrderRequest) => apiClient.post<WorkOrderResponse>('/work-orders/draft', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
    }
  });
}

export function useReserveVehicles() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & ReserveVehiclesRequest) =>
      apiClient.patch<WorkOrderResponse>(`/work-orders/${id}/reserve-vehicles`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
    }
  });
}

export function useFinalizeDraftWorkOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateWorkOrderRequest }) =>
      apiClient.patch<WorkOrderResponse>(`/work-orders/${id}/finalize`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
    }
  });
}

export function useCancelWorkOrderById() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.patch<WorkOrderResponse>(`/work-orders/${id}/cancel`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
    }
  });
}

export function useArchiveWorkOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.patch<WorkOrderResponse>(`/work-orders/${id}/archive`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
    }
  });
}

export function useCreateWorkOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWorkOrderRequest) => apiClient.post<WorkOrderResponse>('/work-orders', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
    }
  });
}

export function useAdvanceWorkOrderStep(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AdvanceWorkOrderStepRequest) => apiClient.patch<WorkOrderResponse>(`/work-orders/${workOrderId}/step`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId, 'work-logs'] });
    }
  });
}

export function useCancelWorkOrder(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.patch<WorkOrderResponse>(`/work-orders/${workOrderId}/cancel`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId] });
    }
  });
}

/**
 * US-06 AC 7, AC 8 (gaps P1, P8) — schedules an entry on the order. The row is created PENDING;
 * recording its receipt is `useConfirmSinglePayment`. Works while the order is still a DRAFT, which
 * is what lets the payments section persist instead of living in browser state.
 */
export function useAddWorkOrderPayment(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePaymentRequest) => apiClient.post<PaymentResponse>(`/work-orders/${workOrderId}/payments`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId, 'payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    }
  });
}

/** US-06 AC 8 (gap P9) — edits one scheduled entry in place; only a PENDING entry is editable. */
export function useUpdateWorkOrderPayment(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ paymentId, data }: { paymentId: string; data: UpdatePaymentRequest }) =>
      apiClient.patch<PaymentResponse>(`/work-orders/${workOrderId}/payments/${paymentId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId, 'payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    }
  });
}

/**
 * US-06 AC 6 (gap P10) — removes a scheduled entry while the order is a DRAFT. The row is
 * cancelled rather than deleted, and drops out of `useWorkOrderPayments` on the next read.
 */
export function useRemoveWorkOrderPayment(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paymentId: string) => apiClient.delete<void>(`/work-orders/${workOrderId}/payments/${paymentId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId, 'payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    }
  });
}

export function useWorkOrderPayments(workOrderId: string, enabled = true) {
  return useQuery({
    queryKey: ['work-orders', workOrderId, 'payments'],
    queryFn: () => apiClient.get<PaymentResponse[]>(`/work-orders/${workOrderId}/payments`),
    enabled: !!workOrderId && enabled
  });
}

export function useWorkOrderPayment(workOrderId: string, paymentId: string) {
  return useQuery({
    queryKey: ['work-orders', workOrderId, 'payments', paymentId],
    queryFn: () => apiClient.get<PaymentResponse>(`/work-orders/${workOrderId}/payments/${paymentId}`),
    enabled: !!workOrderId && !!paymentId
  });
}

export function useConfirmSinglePayment(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paymentId: string) => apiClient.patch<PaymentResponse>(`/work-orders/${workOrderId}/payments/${paymentId}/confirm`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId, 'payments'] });
      // PAY-03: confirming the last outstanding row completes the order, and the gate derives from rows.
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId, 'payment-gates'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    }
  });
}

/**
 * PAY-05 — the account owner cancels one PENDING row of a finalized order with a note. The row is
 * cancelled, never deleted, and drops out of `useWorkOrderPayments` on the next read; the note is
 * read on the timeline. Invalidates what the confirm does: cancelling the last unsettled row
 * completes the order, and the upfront gate derives from the rows.
 */
export function useCancelWorkOrderPayment(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ paymentId, data }: { paymentId: string; data: CancelPaymentRequest }) =>
      apiClient.post<PaymentResponse>(`/work-orders/${workOrderId}/payments/${paymentId}/cancellation`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId, 'payments'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId, 'payment-gates'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    }
  });
}

/** PAY-12 — the account owner splits one pending payment; returns the new rows. */
export function useSplitWorkOrderPayment(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ paymentId, data }: { paymentId: string; data: SplitPaymentRequest }) =>
      apiClient.post<PaymentResponse[]>(`/work-orders/${workOrderId}/payments/${paymentId}/split`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId, 'payments'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId, 'payment-gates'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    }
  });
}

// ──────────────────────────────────────────────
// PAY-10 — payment-details profiles and the send
// ──────────────────────────────────────────────

/** The account's payment-details profiles, by title. Readable by the send roles; `enabled` false skips the read. */
export function usePaymentDetailsProfiles(enabled = true) {
  return useQuery({
    queryKey: ['payment-details'],
    queryFn: () => apiClient.get<PaymentDetailsProfile[]>('/payment-details'),
    enabled
  });
}

export function useSavePaymentDetailsProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id?: string; data: SavePaymentDetailsProfileRequest }) =>
      id
        ? apiClient.put<PaymentDetailsProfile>(`/payment-details/${id}`, data)
        : apiClient.post<PaymentDetailsProfile>('/payment-details', data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payment-details'] })
  });
}

export function useDeletePaymentDetailsProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`/payment-details/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payment-details'] })
  });
}

/** The send dialog's options for one payment: the customer's email and whether Telegram can reach them. */
export function usePaymentDetailsOptions(workOrderId: string, paymentId: string | null) {
  return useQuery({
    queryKey: ['work-orders', workOrderId, 'payments', paymentId, 'payment-details-options'],
    queryFn: () => apiClient.get<PaymentDetailsOptions>(`/work-orders/${workOrderId}/payments/${paymentId}/payment-details/options`),
    enabled: !!workOrderId && !!paymentId,
    staleTime: 0
  });
}

/** The PDF for one profile — read on demand for the dialog's preview; it sends and records nothing. */
export function fetchPaymentDetailsDocument(workOrderId: string, paymentId: string, profileId: string) {
  return apiClient.get<PaymentDetailsDocument>(
    `/work-orders/${workOrderId}/payments/${paymentId}/payment-details/document?profileId=${encodeURIComponent(profileId)}`
  );
}

/**
 * Sends by the requested channels. 200 carries each channel's outcome; when every channel failed the server
 * answers 502 and the client throws, so the caller reads a thrown `ApiError` with status 502 as "all FAILED".
 * A sent channel adds a money row to the timeline.
 */
export function useSendPaymentDetails(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ paymentId, data }: { paymentId: string; data: SendPaymentDetailsRequest }) =>
      apiClient.post<PaymentDetailsSendResult>(`/work-orders/${workOrderId}/payments/${paymentId}/payment-details/send`, data),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
    }
  });
}

// ──────────────────────────────────────────────
// Invoice hooks
// ──────────────────────────────────────────────

export function useInvoice(invoiceId: string) {
  return useQuery({
    queryKey: ['invoices', invoiceId],
    queryFn: () => apiClient.get<InvoiceResponse>(`/v1/invoices/${invoiceId}`),
    enabled: !!invoiceId
  });
}

export function useInvoiceByWorkOrder(workOrderId: string) {
  return useQuery({
    queryKey: ['invoices', 'by-work-order', workOrderId],
    queryFn: () => apiClient.get<InvoiceResponse>(`/v1/invoices/by-work-order/${workOrderId}`),
    enabled: !!workOrderId
  });
}

// REPORT-01 — the authenticated read of the same report the public page renders. The endpoint
// (`WorkOrderController.getCompletionReport`) already permits FLEET_OPERATOR and redacts money
// per ROLE-02 for a caller without WORK_ORDERS_ORDER_MONEY_VIEW, so the operator surface needs no
// new route and no role branch of its own — what comes back is already what that caller may see.
// `getPublic` is deliberately NOT used here: unlike the share-link flow this call is the
// authenticated one and must carry the Authorization header.
export function useCompletionReport(workOrderId: string, enabled = true) {
  return useQuery({
    queryKey: ['work-orders', workOrderId, 'completion-report'],
    queryFn: () => apiClient.get<CompletionReportResponse>(`/work-orders/${workOrderId}/completion-report`),
    enabled: enabled && !!workOrderId
  });
}

export function useIssueCompletionReportShareToken() {
  return useMutation({
    mutationFn: (workOrderId: string) =>
      apiClient.post<CompletionReportShareTokenResponse>(`/work-orders/${workOrderId}/completion-report/share-token`, {})
  });
}

/**
 * REPORT-06 — email the customer's copy as a server-rendered PDF to the customer's address. Money
 * roles only on the server; the client offers the control on the same server answer that gates
 * the detail page's Financials card, never on a role array.
 */
export function useEmailCompletionReport() {
  return useMutation({
    mutationFn: (workOrderId: string) => apiClient.post<EmailedReportResponse>(`/work-orders/${workOrderId}/completion-report/email`, {})
  });
}

export function usePublicCompletionReport(workOrderId: string, token: string) {
  return useQuery({
    queryKey: ['public', 'completion-report', workOrderId, token],
    queryFn: () =>
      apiClient.getPublic<CompletionReportResponse>(
        `/public/work-orders/${workOrderId}/completion-report?token=${encodeURIComponent(token)}`
      ),
    enabled: !!workOrderId && !!token,
    retry: false
  });
}

// ESTIMATE-02 — the estimate's customer-facing document, priced on the server; the staff PDF draws from it.
export function useEstimateDocument(workOrderId: string, enabled = true) {
  return useQuery({
    queryKey: ['work-orders', workOrderId, 'estimate-document'],
    queryFn: () => apiClient.get<EstimateDocument>(`/work-orders/${workOrderId}/estimate-document`),
    enabled: enabled && !!workOrderId,
    staleTime: 0
  });
}

export function useIssueEstimateShareToken() {
  return useMutation({
    mutationFn: (workOrderId: string) => apiClient.post<EstimateShareTokenResponse>(`/work-orders/${workOrderId}/estimate/share-token`, {})
  });
}

/** ESTIMATE-03 — the staff PDF's read: the document, with the quote recorded on the estimate as sent. */
export function useRecordEstimateQuote() {
  return useMutation({
    mutationFn: (workOrderId: string) => apiClient.post<EstimateDocument>(`/work-orders/${workOrderId}/estimate/quote`, {})
  });
}

/** ESTIMATE-04 — the live estimate as a server-rendered PDF, emailed; records the quote as the other sends do. */
/** ESTIMATE-05 (VEK-193) — what a live estimate can be sent by; read only while the share row is shown. */
export function useEstimateSendOptions(workOrderId: string, enabled = true) {
  return useQuery({
    queryKey: ['work-orders-v2', workOrderId, 'estimate-send-options'],
    queryFn: () => apiClient.get<EstimateSendOptions>(`/work-orders/${workOrderId}/estimate/send-options`),
    enabled: enabled && !!workOrderId,
    staleTime: 30_000
  });
}

/** ESTIMATE-05 — the estimate out by the chosen channels; records the quote when any went (a 502 means none did). */
export function useSendEstimate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workOrderId, ...data }: { workOrderId: string } & SendEstimateRequest) =>
      apiClient.post<EstimateSendResult>(`/work-orders/${workOrderId}/estimate/send`, data satisfies SendEstimateRequest),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
    }
  });
}

export function useEmailEstimate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workOrderId, to }: { workOrderId: string; to: string }) =>
      apiClient.post<EmailedReportResponse>(`/work-orders/${workOrderId}/estimate/email`, { to } satisfies EmailEstimateRequest),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
    }
  });
}

/** ESTIMATE-03 — the last quote against today's prices; always fresh, prices move. */
export function useEstimateConversionPreview(workOrderId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['work-orders', workOrderId, 'estimate-conversion-preview'],
    queryFn: () => apiClient.get<EstimateConversionPreview>(`/work-orders/${workOrderId}/estimate/conversion-preview`),
    enabled: enabled && !!workOrderId,
    staleTime: 0,
    gcTime: 0
  });
}

/** ESTIMATE-03 — ESTIMATE → DRAFT in place, holding every entry or none. */
export function useConvertEstimate() {
  const queryClient = useQueryClient();
  return useMutation({
    // PORTAL-04 (VEK-209) — a phone-matched portal estimate converts only with `confirmCustomer: true`.
    mutationFn: ({ workOrderId, confirmCustomer = false }: { workOrderId: string; confirmCustomer?: boolean }) =>
      apiClient.post<WorkOrderDetailV2>(`/work-orders/${workOrderId}/estimate/convert`, confirmCustomer ? { confirmCustomer: true } : {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
    }
  });
}

/** The customer's link. `getPublic`: no Authorization header, never enters the refresh queue. */
/** ESTIMATE-05 — the short estimate link texted to the customer; 404 for an unknown code. */
export function usePublicEstimateShortLink(code: string) {
  return useQuery({
    queryKey: ['public', 'estimate-link', code],
    queryFn: () => apiClient.getPublic<EstimateShortLink>(`/public/estimate-links/${encodeURIComponent(code)}`),
    enabled: !!code,
    retry: false
  });
}

export function usePublicEstimate(workOrderId: string, token: string) {
  return useQuery({
    queryKey: ['public', 'estimate', workOrderId, token],
    queryFn: () => apiClient.getPublic<EstimateDocument>(`/public/work-orders/${workOrderId}/estimate?token=${encodeURIComponent(token)}`),
    enabled: !!workOrderId && !!token,
    retry: false
  });
}

// UI-05 — the API's own build identity, rendered on the footer's second line. `getPublic` rather
// than `get` is deliberate: it attaches no Authorization header and never enters the refresh queue,
// so a cosmetic footer read can never trigger a token refresh or log anyone out. `staleTime:
// Infinity` because the value cannot change while a document is open — the API would have to be
// redeployed, and this client reloads the whole document on every navigation anyway. `retry: false`
// so an API that is down resolves to the unavailable marker promptly instead of retrying behind a
// placeholder.
export function useApiBuildInfo() {
  return useQuery({
    queryKey: ['system', 'version'],
    queryFn: () => apiClient.getPublic<BuildInfoResponse>('/system/version'),
    retry: false,
    staleTime: Infinity
  });
}

function invalidateInvoice(queryClient: ReturnType<typeof useQueryClient>, invoiceId: string) {
  queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] });
  queryClient.invalidateQueries({ queryKey: ['invoices', 'by-work-order'] });
  queryClient.invalidateQueries({ queryKey: ['work-orders'] });
}

export function useUpdateInvoice(invoiceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateInvoiceRequest) => apiClient.patch<InvoiceResponse>(`/v1/invoices/${invoiceId}`, data),
    onSuccess: () => invalidateInvoice(queryClient, invoiceId)
  });
}

export function useAddInvoiceEntry(invoiceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AddInvoiceEntryRequest) => apiClient.post<InvoiceResponse>(`/v1/invoices/${invoiceId}/entries`, data),
    onSuccess: () => invalidateInvoice(queryClient, invoiceId)
  });
}

export function useRemoveInvoiceEntry(invoiceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => apiClient.delete<void>(`/v1/invoices/${invoiceId}/entries/${entryId}`),
    onSuccess: () => invalidateInvoice(queryClient, invoiceId)
  });
}

export function useIssueInvoice(invoiceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post<InvoiceResponse>(`/v1/invoices/${invoiceId}/issue`, {}),
    onSuccess: () => invalidateInvoice(queryClient, invoiceId)
  });
}

// ──────────────────────────────────────────────
// Reservation hooks
// ──────────────────────────────────────────────

export function useCreateReservation() {
  return useMutation({
    mutationFn: (data: CreateReservationRequest) => apiClient.post<ReservationResponse>('/reservations', data)
  });
}

export function useCancelReservation() {
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`/reservations/${id}`)
  });
}

// ──────────────────────────────────────────────
// Geocoding & Distance hooks
// ──────────────────────────────────────────────

// Forward geocoding moved to `components/vektra/addressSearch.ts` (ADDR-04): it runs on submit,
// in stages, across three providers, and is imperative rather than a query.

/**
 * Road distance in metres from one origin to many destinations, in a SINGLE request (ADDR-01).
 *
 * OSRM's Table service answers the whole matrix at once; `sources=0` restricts it to the one row
 * we need. Measuring six search results costs one request here, where six `useCalculateDistance`
 * calls would cost six against a demo server that asks for restraint.
 *
 * Returns metres positionally aligned with `destinations`; an entry is null where OSRM could not
 * route to that point. Callers must fail open when the query itself errors — a routing outage may
 * widen the offer, never empty it.
 */
export function useRoadDistances(originLat?: number, originLng?: number, destinations?: Array<{ lat: number; lng: number }>) {
  const points = destinations ?? [];
  const enabled = originLat != null && originLng != null && points.length > 0;
  return useQuery({
    queryKey: ['road-distances', originLat, originLng, points.map((p) => `${p.lat},${p.lng}`).join(';')],
    queryFn: async (): Promise<Array<number | null>> => {
      const coords = [`${originLng},${originLat}`, ...points.map((p) => `${p.lng},${p.lat}`)].join(';');
      const res = await fetch(`https://router.project-osrm.org/table/v1/driving/${coords}?sources=0&annotations=distance`);
      const data = await res.json();
      // distances[0] is the origin row: [self, dest1, dest2, ...] — drop the leading self-distance.
      const row: unknown = data?.distances?.[0];
      if (!Array.isArray(row)) throw new Error('OSRM table response carried no distance row');
      return points.map((_, i) => {
        const metres = row[i + 1];
        return typeof metres === 'number' ? metres : null;
      });
    },
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000
  });
}

/**
 * One origin→destination driving route: its length in metres, its duration in seconds, and the
 * path itself.
 *
 * `overview=full` replaced `overview=false` (UI-02). The figures alone were enough for a caller
 * that only wanted a number, but the work-order location map draws the leg, and a straight line
 * between yard and site asserts a path nobody drives. `geometry` is OSRM's default encoded
 * polyline at precision 5 — decode it with `utils/polyline.ts`.
 *
 * `geometry` is null when OSRM answered without one; callers draw the provisional line in that
 * case rather than dropping the route. This runs against the public demo server, so callers issue
 * it on an explicit user action — never once per row of a list.
 */
export function useCalculateDistance(fromLat?: number, fromLng?: number, toLat?: number, toLng?: number) {
  return useQuery({
    queryKey: ['distance', fromLat, fromLng, toLat, toLng],
    queryFn: async (): Promise<{ distance: number; duration: number; geometry: string | null }> => {
      const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${fromLng},${fromLat};${toLng},${toLat}?overview=full`);
      const data = await res.json();
      const route = data?.routes?.[0];
      if (!route) throw new Error('OSRM route response carried no route');
      return {
        distance: route.distance as number,
        duration: route.duration as number,
        geometry: typeof route.geometry === 'string' ? route.geometry : null
      };
    },
    enabled: fromLat != null && fromLng != null && toLat != null && toLng != null,
    retry: false,
    staleTime: 5 * 60 * 1000
  });
}

// ──────────────────────────────────────────────
// Customer hooks
// ──────────────────────────────────────────────

export function useCustomers(page = 0, size = 20, search?: string) {
  return useQuery({
    queryKey: ['customers', page, size, search],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      if (search) params.append('search', search);
      return apiClient.get<PageResponse<CustomerResponse>>(`/customers?${params}`);
    }
  });
}

export function useCustomer(id: string) {
  return useQuery({
    queryKey: ['customers', id],
    queryFn: () => apiClient.get<CustomerResponse>(`/customers/${id}`),
    enabled: !!id
  });
}

/**
 * CUST-12 — the Customers list's Phone reset: the customer's phone becomes `+99` + the Unix seconds and is
 * unverified; the former number is free for a new customer. The list is invalidated so the row redraws.
 */
export function useResetCustomerPhone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (customerId: string) => apiClient.post<CustomerResponse>(`/customers/${customerId}/phone-reset`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    }
  });
}

/** CUST-03 — issue a single-use profile-update link; the server revokes the customer's older one. */
export function useIssueCustomerProfileLink(customerId: string) {
  return useMutation({
    mutationFn: () => apiClient.post<CustomerProfileLinkIssued>(`/customers/${customerId}/profile-links`, {})
  });
}

/** CUST-03 — what the customer changed through their links, newest first. */
export function useCustomerProfileChanges(customerId: string, enabled = true) {
  return useQuery({
    queryKey: ['customers', customerId, 'profile-changes'],
    queryFn: () => apiClient.get<CustomerProfileChange[]>(`/customers/${customerId}/profile-links`),
    enabled: !!customerId && enabled
  });
}

/** CUST-03 — the public read behind `/p/:code`. Never cached across codes, never retried. */
export function usePublicCustomerProfile(code: string) {
  return useQuery({
    queryKey: ['public', 'customer-profile', code],
    queryFn: () => apiClient.getPublic<PublicCustomerProfile>(`/public/customer-profile/${encodeURIComponent(code)}`),
    enabled: !!code,
    retry: false,
    staleTime: 0
  });
}

export function useSubmitPublicCustomerProfile(code: string) {
  return useMutation({
    mutationFn: (body: SubmitCustomerProfileRequest) =>
      apiClient.patchPublic<PublicCustomerProfile>(`/public/customer-profile/${encodeURIComponent(code)}`, body)
  });
}

/** UI-21 (VEK-100) — the landing's Request a demo. `postPublic`: no Authorization header, never the refresh queue. */
export function useSubmitDemoRequest() {
  return useMutation({
    mutationFn: (body: DemoRequestRequest) => apiClient.postPublic<DemoRequestResponse>('/public/demo-requests', body)
  });
}

/**
 * CUST-04 — the Telegram start link for `/p/:code`, asked for once while the profile link is live.
 * The server hands back the same code on every call; the result is kept after submit so the block
 * stays on the confirmation.
 */
export function useTelegramConnectLink(code: string, enabled: boolean) {
  return useQuery({
    queryKey: ['public', 'customer-profile', code, 'telegram-connect'],
    queryFn: () => apiClient.postPublic<TelegramConnectLink>(`/public/customer-profile/${encodeURIComponent(code)}/telegram-connect`, {}),
    enabled: !!code && enabled,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false
  });
}

/**
 * CUST-04 — whether the customer's Telegram chat is connected and which consents it gave. CUST-10 — the
 * desk connect dialog passes `refetchInterval` so the card flips as soon as the customer scans.
 */
export function useCustomerTelegramStatus(customerId: string, enabled = true, refetchInterval: number | false = false) {
  return useQuery({
    queryKey: ['customers', customerId, 'telegram'],
    queryFn: () => apiClient.get<CustomerTelegramStatus>(`/customers/${customerId}/telegram`),
    enabled: !!customerId && enabled,
    refetchInterval
  });
}

/** CUST-10 — the customer's 15-minute desk code: the same code while it is live, a fresh one after. */
export function useCreateTelegramConnectCode(customerId: string) {
  return useMutation({
    mutationFn: () => apiClient.post<TelegramConnectCode>(`/customers/${customerId}/telegram/connect-code`, {})
  });
}

// NOTIF-01 (VEK-117) — my staff Telegram link. Keyed under 'me', 'telegram' so marking notifications read
// never refetches it; the connect dialog passes `refetchInterval` so the panel flips as soon as /start lands.
export function useMyTelegramStatus(refetchInterval: number | false = false) {
  return useQuery({
    queryKey: ['me', 'telegram'],
    queryFn: () => apiClient.get<StaffTelegramStatus>('/users/me/telegram'),
    refetchInterval
  });
}

/** NOTIF-01 — my 15-minute staff-bot connect code: the same code while it is live, a fresh one after; 400 while linked. */
export function useCreateMyTelegramConnectCode() {
  return useMutation({
    mutationFn: () => apiClient.post<TelegramConnectCode>('/users/me/telegram/connect-code', {})
  });
}

/** NOTIF-01 — disconnect my chat from the staff bot; notifications go back to push. */
export function useDisconnectMyTelegram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.delete<void>('/users/me/telegram'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me', 'telegram'] })
  });
}

/** CUST-05 — whether the SMS gateway phone is online; polled while the share dialog is open. */
export function useSmsGatewayStatus(enabled = true) {
  return useQuery({
    queryKey: ['sms-gateway', 'status'],
    queryFn: () => apiClient.get<SmsGatewayStatus>('/sms-gateway/status'),
    enabled,
    staleTime: 0,
    refetchInterval: enabled ? 30_000 : false
  });
}

/** CUST-05 — queue the dialog's live profile link by SMS; the server writes the text and never issues a link. */
export function useSendProfileLinkSms(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => apiClient.post<SmsMessage>(`/customers/${customerId}/sms/profile-link`, { code }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customers', customerId, 'sms'] })
  });
}

/** CUST-06 — the customer's live profile link (expiry, where it was texted), or null; never the code. */
export function useLiveProfileLink(customerId: string, enabled = true) {
  return useQuery({
    queryKey: ['customers', customerId, 'profile-links', 'live'],
    queryFn: () => apiClient.get<LiveProfileLink | null>(`/customers/${customerId}/profile-links/live`),
    enabled: !!customerId && enabled,
    staleTime: 0
  });
}

/** CUST-06 — text the customer a fresh phone-verification link, verified or not; a refusal is an error. */
export function useResendPhoneVerification(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post<SmsMessage>(`/customers/${customerId}/phone-verification`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers', customerId, 'sms'] });
      queryClient.invalidateQueries({ queryKey: ['customers', customerId, 'profile-links', 'live'] });
    }
  });
}

/** CUST-05 — the customer's SMS, newest first. */
export function useCustomerSms(customerId: string, enabled = true) {
  return useQuery({
    queryKey: ['customers', customerId, 'sms'],
    queryFn: () => apiClient.get<SmsMessage[]>(`/customers/${customerId}/sms`),
    enabled: !!customerId && enabled
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCustomerRequest) => apiClient.post<CustomerResponse>('/customers', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    }
  });
}

export function useUpdateCustomer(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateCustomerRequest) => apiClient.put<CustomerResponse>(`/customers/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customers', id] });
    }
  });
}

export function useCustomerAddresses(customerId: string) {
  return useQuery({
    queryKey: ['customers', customerId, 'addresses'],
    queryFn: () => apiClient.get<CustomerAddressResponse[]>(`/customers/${customerId}/addresses`),
    enabled: !!customerId
  });
}

export function useCreateCustomerAddress(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCustomerAddressRequest) => apiClient.post<CustomerAddressResponse>(`/customers/${customerId}/addresses`, data),
    // Seed before invalidating: invalidateQueries only starts a refetch, so the selection-reconciliation
    // effect in the work-order create screen (create.tsx:620-624) — which reconciles against this exact
    // query's list — would otherwise re-run before the new row lands and reassign the selection to the
    // primary address. CreateWO-Functional-Spec §5.3 requires the new address prepended and auto-selected.
    onSuccess: (created) => {
      queryClient.setQueryData<CustomerAddressResponse[]>(['customers', customerId, 'addresses'], (previous) => [
        created,
        ...(previous ?? [])
      ]);
      queryClient.invalidateQueries({ queryKey: ['customers', customerId, 'addresses'] });
    }
  });
}

export function useUpdateCustomerAddress(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ addressId, data }: { addressId: string; data: UpdateCustomerAddressRequest }) =>
      apiClient.put<CustomerAddressResponse>(`/customers/${customerId}/addresses/${addressId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers', customerId, 'addresses'] });
    }
  });
}

export function useDeleteCustomerAddress(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (addressId: string) => apiClient.delete<void>(`/customers/${customerId}/addresses/${addressId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers', customerId, 'addresses'] });
    }
  });
}

// ──────────────────────────────────────────────
// Maintenance hooks
// ──────────────────────────────────────────────

export function useMaintenanceRecords(page = 0, size = 20, vehicleId?: string, enabled = true) {
  return useQuery({
    queryKey: ['maintenance', page, size, vehicleId],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      if (vehicleId) params.append('vehicleId', vehicleId);
      return apiClient.get<PageResponse<MaintenanceResponse>>(`/maintenance?${params}`);
    },
    enabled
  });
}

/** MAINT-04 — the account-wide record list, filtered and ordered on the server before paging. */
export function useMaintenanceList(page: number, size: number, filters: MaintenanceListFilters) {
  return useQuery({
    queryKey: ['maintenance', 'list', page, size, filters],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      filters.statuses?.forEach((s) => params.append('statuses', s));
      if (filters.search?.trim()) params.append('search', filters.search.trim());
      if (filters.vehicleType) params.append('vehicleType', filters.vehicleType);
      if (filters.cycleCode) params.append('cycleCode', filters.cycleCode);
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.dueState) params.append('dueState', filters.dueState);
      return apiClient.get<PageResponse<MaintenanceResponse>>(`/maintenance?${params}`);
    },
    placeholderData: keepPreviousData
  });
}

/** MAINT-04 — the tab and KPI counts under the list's search, type, cycle and severity filters. */
export function useMaintenanceOverviewSummary(filters: Omit<MaintenanceListFilters, 'statuses' | 'dueState'>) {
  return useQuery({
    queryKey: ['maintenance', 'overview-summary', filters],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.search?.trim()) params.append('search', filters.search.trim());
      if (filters.vehicleType) params.append('vehicleType', filters.vehicleType);
      if (filters.cycleCode) params.append('cycleCode', filters.cycleCode);
      if (filters.severity) params.append('severity', filters.severity);
      return apiClient.get<MaintenanceOverviewSummary>(`/maintenance/overview/summary?${params}`);
    },
    placeholderData: keepPreviousData
  });
}

/** MAINT-04 — the account's vehicles with their four cycles, worst first. */
export function useMaintenanceFleet(
  page: number,
  size: number,
  search?: string,
  vehicleType?: VehicleType,
  cycleCode?: MaintenanceCycleCode,
  withinDays?: number
) {
  return useQuery({
    queryKey: ['maintenance', 'fleet', page, size, search, vehicleType, cycleCode, withinDays],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      if (search?.trim()) params.append('search', search.trim());
      if (vehicleType) params.append('vehicleType', vehicleType);
      if (cycleCode) params.append('cycleCode', cycleCode);
      if (withinDays != null) params.append('withinDays', String(withinDays));
      return apiClient.get<PageResponse<MaintenanceFleetVehicle>>(`/maintenance/fleet?${params}`);
    },
    placeholderData: keepPreviousData
  });
}

export function useMaintenance(id: string) {
  return useQuery({
    queryKey: ['maintenance', id],
    queryFn: () => apiClient.get<MaintenanceResponse>(`/maintenance/${id}`),
    enabled: !!id
  });
}

export function useCreateMaintenance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateMaintenanceRequest) => apiClient.post<MaintenanceResponse>('/maintenance', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
    }
  });
}

export function useUpdateMaintenanceStatus(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: string) => apiClient.patch<MaintenanceResponse>(`/maintenance/${id}/status?status=${status}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance', id] });
      // MAINT-01 — a status change moves the vehicle's status and, on completion, its cycle rows.
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    }
  });
}

// MAINT-01 (VEK-143) — a vehicle's maintenance cycles. The rows themselves travel on GET /vehicles/{id}.

export function useStartMaintenanceCycle(vehicleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cycleCode: MaintenanceCycleCode) =>
      apiClient.post<MaintenanceResponse>(`/maintenance/vehicle/${vehicleId}/cycles/${cycleCode}/start`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
    }
  });
}

export function useSetMaintenanceDueTarget(vehicleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cycleCode, ...body }: { cycleCode: MaintenanceCycleCode } & SetMaintenanceDueTargetRequest) =>
      apiClient.put<VehicleMaintenanceCycleStatus>(`/maintenance/vehicle/${vehicleId}/cycles/${cycleCode}/due-target`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', vehicleId] });
    }
  });
}

export function useClearMaintenanceDueTarget(vehicleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cycleCode: MaintenanceCycleCode) =>
      apiClient.delete<VehicleMaintenanceCycleStatus>(`/maintenance/vehicle/${vehicleId}/cycles/${cycleCode}/due-target`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', vehicleId] });
    }
  });
}

export function useUpdateMaintenance(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateMaintenanceRequest) => apiClient.patch<MaintenanceResponse>(`/maintenance/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance', id] });
    }
  });
}

// MAINT-03 — the checklist dialog and the widgets act on whichever record they show, so the id travels with the call.

/** Invalidates what a maintenance write can move: the record, the lists, and the vehicle's summary and history. */
function invalidateMaintenance(queryClient: ReturnType<typeof useQueryClient>, recordId?: string) {
  queryClient.invalidateQueries({ queryKey: ['maintenance'] });
  if (recordId) queryClient.invalidateQueries({ queryKey: ['maintenance', recordId] });
  queryClient.invalidateQueries({ queryKey: ['vehicles'] });
}

export function useSaveMaintenanceChecklist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & SaveMaintenanceChecklistRequest) =>
      apiClient.put<MaintenanceResponse>(`/maintenance/${id}/checklist`, body),
    onSuccess: (_data, vars) => invalidateMaintenance(queryClient, vars.id)
  });
}

export function useChangeMaintenanceStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, createRepairForFailures }: { id: string; status: MaintenanceStatus; createRepairForFailures?: boolean }) => {
      const params = new URLSearchParams({ status });
      if (createRepairForFailures) params.set('createRepairForFailures', 'true');
      return apiClient.patch<MaintenanceResponse>(`/maintenance/${id}/status?${params.toString()}`, {});
    },
    onSuccess: (_data, vars) => invalidateMaintenance(queryClient, vars.id)
  });
}

export function useScheduleMaintenanceCycle(vehicleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cycleCode, ...body }: { cycleCode: MaintenanceCycleCode } & ScheduleMaintenanceRequest) =>
      apiClient.post<MaintenanceResponse>(`/maintenance/vehicle/${vehicleId}/cycles/${cycleCode}/schedule`, body),
    onSuccess: (data) => invalidateMaintenance(queryClient, data?.id)
  });
}

export function useUpdateMaintenanceWindow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & UpdateMaintenanceWindowRequest) =>
      apiClient.patch<MaintenanceResponse>(`/maintenance/${id}/window`, body),
    onSuccess: (_data, vars) => invalidateMaintenance(queryClient, vars.id)
  });
}

export function useVehicleMaintenanceHistory(vehicleId: string, size = 5, enabled = true) {
  return useQuery({
    queryKey: ['maintenance', 'vehicle', vehicleId, 'history', size],
    queryFn: () => apiClient.get<PageResponse<MaintenanceHistoryEntry>>(`/maintenance/vehicle/${vehicleId}/history?page=0&size=${size}`),
    enabled: enabled && !!vehicleId
  });
}

export function useDeleteMaintenance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`/maintenance/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
    }
  });
}

export function useVehicleUpcomingMaintenance(vehicleId: string, enabled = true) {
  return useQuery({
    queryKey: ['maintenance', 'vehicle', vehicleId, 'upcoming'],
    queryFn: () => apiClient.get<MaintenanceResponse[]>(`/maintenance/vehicle/${vehicleId}/upcoming`),
    enabled: enabled && !!vehicleId
  });
}

export function useVehicleOverdueMaintenance(vehicleId: string, enabled = true) {
  return useQuery({
    queryKey: ['maintenance', 'vehicle', vehicleId, 'overdue'],
    queryFn: () => apiClient.get<MaintenanceResponse[]>(`/maintenance/vehicle/${vehicleId}/overdue`),
    enabled: enabled && !!vehicleId
  });
}

export function useVehicleOpenIssues(vehicleId: string, enabled = true) {
  return useQuery({
    queryKey: ['maintenance', 'vehicle', vehicleId, 'open-issues'],
    queryFn: () => apiClient.get<MaintenanceResponse[]>(`/maintenance/vehicle/${vehicleId}/open-issues`),
    enabled: enabled && !!vehicleId
  });
}

// ──────────────────────────────────────────────
// Maintenance Cycle Configs (PM_A/B/C/SEASONAL)
// ──────────────────────────────────────────────

export function useMaintenanceCycleConfigs(vehicleType?: string, vehicleId?: string, page = 0, size = 50) {
  return useQuery({
    queryKey: ['maintenance', 'cycle-configs', vehicleType, vehicleId, page, size],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      if (vehicleType) params.append('vehicleType', vehicleType);
      if (vehicleId) params.append('vehicleId', vehicleId);
      return apiClient.get<PageResponse<MaintenanceCycleConfigDTO>>(`/maintenance/cycle-configs?${params}`);
    },
    enabled: !!vehicleType || !!vehicleId
  });
}

export function useMaintenanceCycleConfig(id: string) {
  return useQuery({
    queryKey: ['maintenance', 'cycle-configs', id],
    queryFn: () => apiClient.get<MaintenanceCycleConfigDTO>(`/maintenance/cycle-configs/${id}`),
    enabled: !!id
  });
}

export function useCreateMaintenanceCycleConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateMaintenanceCycleConfigRequest) =>
      apiClient.post<MaintenanceCycleConfigDTO>('/maintenance/cycle-configs', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance', 'cycle-configs'] });
    }
  });
}

export function useUpdateMaintenanceCycleConfig(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateMaintenanceCycleConfigRequest) =>
      apiClient.patch<MaintenanceCycleConfigDTO>(`/maintenance/cycle-configs/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance', 'cycle-configs'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance', 'cycle-configs', id] });
    }
  });
}

export function useDeleteMaintenanceCycleConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`/maintenance/cycle-configs/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance', 'cycle-configs'] });
    }
  });
}

// ──────────────────────────────────────────────
// Inspection Templates (CHECK_IN / RETURN_CHECK_IN)
// ──────────────────────────────────────────────

export function useInspectionTemplates(vehicleType?: string, vehicleId?: string, page = 0, size = 50) {
  return useQuery({
    queryKey: ['maintenance', 'inspection-templates', vehicleType, vehicleId, page, size],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      if (vehicleType) params.append('vehicleType', vehicleType);
      if (vehicleId) params.append('vehicleId', vehicleId);
      return apiClient.get<PageResponse<VehicleInspectionTemplateDTO>>(`/maintenance/inspection-templates?${params}`);
    },
    enabled: !!vehicleType || !!vehicleId
  });
}

export function useInspectionTemplate(id: string) {
  return useQuery({
    queryKey: ['maintenance', 'inspection-templates', id],
    queryFn: () => apiClient.get<VehicleInspectionTemplateDTO>(`/maintenance/inspection-templates/${id}`),
    enabled: !!id
  });
}

export function useResolvedInspectionTemplate(vehicleId: string, triggerStep?: InspectionTriggerStep) {
  return useQuery({
    queryKey: ['maintenance', 'inspection-templates', 'resolved', vehicleId, triggerStep],
    queryFn: () => {
      const params = new URLSearchParams({ vehicleId, triggerStep: triggerStep! });
      return apiClient.get<ResolvedInspectionTemplateDTO>(`/maintenance/inspection-templates/resolved?${params}`);
    },
    enabled: !!vehicleId && !!triggerStep
  });
}

export function useCreateInspectionTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateVehicleInspectionTemplateRequest) =>
      apiClient.post<VehicleInspectionTemplateDTO>('/maintenance/inspection-templates', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance', 'inspection-templates'] });
    }
  });
}

export function useUpdateInspectionTemplate(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateVehicleInspectionTemplateRequest) =>
      apiClient.patch<VehicleInspectionTemplateDTO>(`/maintenance/inspection-templates/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance', 'inspection-templates'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance', 'inspection-templates', id] });
    }
  });
}

export function useDeleteInspectionTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`/maintenance/inspection-templates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance', 'inspection-templates'] });
    }
  });
}

// ──────────────────────────────────────────────
// Maintenance Defaults Seeding
// ──────────────────────────────────────────────

export function useSeedMaintenanceDefaults() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SeedMaintenanceDefaultsRequest) => apiClient.post<MaintenanceSeedResultDTO>('/maintenance/seed-defaults', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance', 'cycle-configs'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance', 'inspection-templates'] });
    }
  });
}

export function useMaintenanceDefaultsAvailability() {
  return useQuery({
    queryKey: ['maintenance', 'seed-defaults', 'availability'],
    queryFn: () => apiClient.get<MaintenanceDefaultsAvailabilityDTO>('/maintenance/seed-defaults/availability'),
    // Availability is shipped with the build — safe to cache for the session.
    staleTime: Infinity
  });
}

// ──────────────────────────────────────────────
// Work Order Inspections (CHECK_IN / RETURN_CHECK_IN)
// ──────────────────────────────────────────────

export function useWorkOrderItemResolvedInspectionTemplate(workOrderId: string, itemId: string, triggerStep?: InspectionTriggerStep) {
  return useQuery({
    queryKey: ['work-orders', workOrderId, 'items', itemId, 'inspection-template', triggerStep],
    queryFn: () => {
      const params = new URLSearchParams({ triggerStep: triggerStep! });
      return apiClient.get<ResolvedInspectionTemplateDTO>(`/work-orders/${workOrderId}/items/${itemId}/inspection?${params}`);
    },
    enabled: !!workOrderId && !!itemId && !!triggerStep
  });
}

export function useSubmitWorkOrderInspection(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SubmitWorkOrderInspectionRequest) =>
      apiClient.post<WorkOrderInspectionDTO>(`/work-orders/${workOrderId}/items/${itemId}/inspections`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId, 'inspections'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    }
  });
}

export function useWorkOrderInspections(workOrderId: string) {
  return useQuery({
    queryKey: ['work-orders', workOrderId, 'inspections'],
    queryFn: () => apiClient.get<WorkOrderInspectionDTO[]>(`/work-orders/${workOrderId}/inspections`),
    enabled: !!workOrderId
  });
}

// ──────────────────────────────────────────────
// Vehicle — clear BROKEN status
// ──────────────────────────────────────────────

export function useClearVehicleBrokenStatus(vehicleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ClearBrokenStatusRequest) => apiClient.post<VehicleResponse>(`/vehicles/${vehicleId}/clear-broken-status`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', vehicleId] });
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
    }
  });
}

// ──────────────────────────────────────────────
// Notification hooks
// ──────────────────────────────────────────────

export function useNotifications(page = 0, size = 20) {
  // NOTIF-07 (VEK-168) — the server renders each row in Accept-Language, so a header switch must refetch.
  const { i18n } = useTranslation();
  return useQuery({
    queryKey: ['notifications', page, size, i18n.language],
    queryFn: () => apiClient.get<PageResponse<NotificationResponse>>(`/notifications?page=${page}&size=${size}`)
  });
}

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => apiClient.get<number>('/notifications/unread-count'),
    refetchInterval: 30_000 // poll every 30s
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.patch<void>(`/notifications/${id}/read`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.patch<void>('/notifications/read-all', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  });
}

// PUSH-01 — Web Push. Keyed under 'push', not 'notifications', so marking notifications read never
// refetches the server's push config.
export function usePushConfig() {
  return useQuery({
    queryKey: ['push', 'config'],
    queryFn: () => apiClient.get<PushConfigResponse>('/push/config'),
    staleTime: Infinity
  });
}

export function useRegisterPush() {
  return useMutation({
    mutationFn: (request: RegisterPushSubscriptionRequest) => apiClient.put<void>('/push/subscriptions', request)
  });
}

export function useUnregisterPush() {
  return useMutation({
    mutationFn: (endpoint: string) => apiClient.delete<void>(`/push/subscriptions?endpoint=${encodeURIComponent(endpoint)}`)
  });
}

// ──────────────────────────────────────────────
// User hooks
// ──────────────────────────────────────────────

export function useUsers(page = 0, size = 20) {
  return useQuery({
    queryKey: ['users', page, size],
    queryFn: () => apiClient.get<PageResponse<UserResponse>>(`/users?page=${page}&size=${size}`)
  });
}

/** Fetches all users (up to 100) and filters client-side by the given roles. */
export function useUsersByRole(roles: string[], enabled = true) {
  return useQuery({
    queryKey: ['users', 'by-role', roles],
    queryFn: async () => {
      const res = await apiClient.get<PageResponse<UserResponse>>('/users?size=100');
      return res.data.filter((u) => roles.includes(u.role));
    },
    enabled,
    staleTime: 2 * 60 * 1000
  });
}

export function useUser(id: string) {
  return useQuery({
    queryKey: ['users', id],
    queryFn: () => apiClient.get<UserResponse>(`/users/${id}`),
    enabled: !!id
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateUserRequest) => apiClient.post<UserResponse>('/users', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    }
  });
}

export function useUpdateUser(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateUserRequest) => apiClient.put<UserResponse>(`/users/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', id] });
    }
  });
}

// USER-03 (VEK-256) — the signed-in user's own profile. `useUser(id)` above targets /users/{id}, which only the
// owner column may read; every seat may read and edit itself through /users/me.
export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => apiClient.get<UserResponse>('/users/me')
  });
}

export function useUpdateMe() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateUserRequest) => apiClient.put<UserResponse>('/users/me', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    }
  });
}

export function useChangeMyPassword() {
  return useMutation({
    mutationFn: (data: ChangePasswordRequest) => apiClient.put<void>('/users/me/password', data)
  });
}

// ──────────────────────────────────────────────
// File hooks
// ──────────────────────────────────────────────

export function useUploadFile() {
  return useMutation({
    mutationFn: (formData: FormData) => apiClient.upload<FileUploadResponse>('/v1/files', formData)
  });
}

export function useUploadFiles() {
  return useMutation({
    mutationFn: (formData: FormData) => apiClient.upload<FileUploadResponse[]>('/v1/files/batch', formData)
  });
}

// ──────────────────────────────────────────────
// Onboarding hooks
// ──────────────────────────────────────────────

export function useOnboardingStatus() {
  return useQuery({
    queryKey: ['onboarding', 'status'],
    queryFn: () => apiClient.get<OnboardingStatusResponse>('/v1/onboarding/status')
  });
}

/** PORTAL-03 (VEK-208) — the account's white-label portal (base URL, enabled), or null; only the four roles may ask. */
export function usePortalIntegration(enabled: boolean) {
  return useQuery({
    queryKey: ['portal-integration'],
    queryFn: () => apiClient.get<PortalIntegrationSummary | null>('/portal-integration'),
    enabled
  });
}

/** CUST-13 (VEK-258) — the account's customer-messages language; only the owner roles may read it. */
export function useCustomerMessagesLanguage(enabled: boolean) {
  return useQuery({
    queryKey: ['customer-messages-language'],
    queryFn: () => apiClient.get<CustomerMessagesLanguageDTO>('/v1/business-account/customer-messages-language'),
    enabled
  });
}

export function useUpdateCustomerMessagesLanguage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (language: CustomerMessagesLanguageCode) =>
      apiClient.put<CustomerMessagesLanguageDTO>('/v1/business-account/customer-messages-language', { language }),
    onSuccess: (data) => {
      queryClient.setQueryData(['customer-messages-language'], data);
    }
  });
}

// ACCT-03 (VEK-257) — the caller's own business account as a company profile (every seat reads, the owner
// column writes). A rename must reach the header switcher, which draws its label from `useAccounts()`
// (`['auth', 'accounts']`), so the update invalidates that query too.
export function useBusinessAccount() {
  return useQuery({
    queryKey: ['business-account', 'me'],
    queryFn: () => apiClient.get<BusinessAccountResponse>('/v1/business-accounts/me')
  });
}

export function useUpdateBusinessAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateBusinessAccountRequest) => apiClient.put<BusinessAccountResponse>('/v1/business-accounts/me', data),
    onSuccess: (data) => {
      queryClient.setQueryData(['business-account', 'me'], data);
      queryClient.invalidateQueries({ queryKey: ['auth', 'accounts'] });
    }
  });
}

// ──────────────────────────────────────────────
// Dashboard financial summary hooks
// ──────────────────────────────────────────────

/** DASH-09 — the dashboard's Orders row: counts only, readable by the operational roles too. */
export function useOperationalSummary(period: FinancialPeriod, offset: number, compareTo: FinancialComparison, enabled = true) {
  return useQuery({
    queryKey: ['dashboard', 'operational-summary', period, offset, compareTo],
    queryFn: () => {
      const params = new URLSearchParams({ period, offset: String(offset), compareTo });
      return apiClient.get<OperationalSummaryResponse>(`/v1/dashboard/operational-summary?${params}`);
    },
    enabled,
    placeholderData: (prev) => prev,
    retry: false
  });
}

// HOME-01 (VEK-95) — the day agenda on /home. The server counts days in the account's operational time zone;
// the browser's zone is sent only as the fallback for an account that has none. Refreshed every minute so the
// "now" divider and the overdue minutes move while the page stays open.
export function useHomeAgenda(day: AgendaDay, enabled = true) {
  return useQuery({
    queryKey: ['home', 'agenda', day],
    queryFn: () => {
      const params = new URLSearchParams({ day });
      const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (zone) params.set('zone', zone);
      return apiClient.get<HomeAgendaResponse>(`/v1/home/agenda?${params}`);
    },
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: false
  });
}

export function useFinancialSummary(
  period: FinancialPeriod,
  offset: number,
  compareTo: FinancialComparison,
  currency?: string,
  enabled = true
) {
  return useQuery({
    queryKey: ['dashboard', 'financial-summary', period, offset, compareTo, currency],
    queryFn: () => {
      // DASH-08 — offset moves the window whole periods back; compareTo picks the base.
      const params = new URLSearchParams({ period, offset: String(offset), compareTo });
      if (currency) params.append('currency', currency);
      return apiClient.get<FinancialSummaryResponse>(`/v1/dashboard/financial-summary?${params}`);
    },
    enabled,
    // Keep the previous summary visible while a period/currency switch loads.
    placeholderData: (prev) => prev,
    // The endpoint may not be deployed yet — fail soft without retry storms;
    // the dashboard section hides itself on error.
    retry: false
  });
}

// ──────────────────────────────────────────────
// Warehouse hooks
// ──────────────────────────────────────────────

export function useWarehouses(page = 0, size = 20) {
  return useQuery({
    queryKey: ['warehouses', page, size],
    queryFn: () => apiClient.get<PageResponse<WarehouseResponse>>(`/v1/warehouses?page=${page}&size=${size}`)
  });
}

export function useWarehouse(id: string) {
  return useQuery({
    queryKey: ['warehouses', id],
    queryFn: () => apiClient.get<WarehouseResponse>(`/v1/warehouses/${id}`),
    enabled: !!id
  });
}

/**
 * The account's main yard — the delivery origin every distance is measured from (ADDR-01).
 *
 * The derivation is the one that already lived inline on the work-order create page: the row
 * flagged primary, else the first row. It is kept identical on purpose, so moving that call site
 * onto this hook cannot shift the deliverability verdict it feeds. The server keeps `isPrimary`
 * unique by unsetting the previous holder on promotion (WarehouseServiceImpl:128-130).
 *
 * There is no `/v1/warehouses/primary` endpoint, so this reads a page and filters. The page is
 * large enough that the fallback is unreachable for a realistic account; past it, `rows[0]` is
 * whatever the server sorted first and nothing on screen would admit it (BACKLOG).
 */
export function usePrimaryWarehouse() {
  const query = useWarehouses(0, 200);
  const rows = query.data?.data ?? [];
  const warehouse = rows.find((w) => w.isPrimary) ?? rows[0];
  return { ...query, warehouse };
}

export function useCreateWarehouse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWarehouseRequest) => apiClient.post<WarehouseResponse>('/v1/warehouses', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    }
  });
}

export function useUpdateWarehouse(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateWarehouseRequest) => apiClient.put<WarehouseResponse>(`/v1/warehouses/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      queryClient.invalidateQueries({ queryKey: ['warehouses', id] });
    }
  });
}

export function useDeleteWarehouse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`/v1/warehouses/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    }
  });
}

// WH-01 (VEK-203) — promotion from the Warehouses list. The server demotes the previous primary
// in the same transaction (WarehouseServiceImpl.setPrimary); `PUT /v1/warehouses/{id}` ignores
// `isPrimary`, so this is the only way to move the flag.
export function useSetPrimaryWarehouse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.put<WarehouseResponse>(`/v1/warehouses/${id}/set-primary`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
    }
  });
}

// ──────────────────────────────────────────────
// Billing Config hooks
// ──────────────────────────────────────────────

export function useBillingConfigs(page = 0, size = 100, groupType?: string) {
  return useQuery({
    queryKey: ['billing-configs', page, size, groupType],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      if (groupType) params.append('groupType', groupType);
      return apiClient.get<PageResponse<BillingConfigResponse>>(`/v1/billing-configs/vehicle-types?${params}`);
    }
  });
}

/**
 * One vehicle type's billing configuration — the add-vehicle dialog's source for `minimalHoursRent`,
 * which is what turns a proposed rent start into a proposed rent end (US-03 AC 1).
 *
 * The key sits under the same `['billing-configs', …]` prefix every mutation in this section already
 * invalidates, so an admin editing the configuration refreshes the dialog with no second rule.
 *
 * **Deliberately not shaped like `useOperationalSchedule` below.** That one turns a 404 into `null`
 * because an account may legitimately have no schedule yet. A vehicle type with no billing
 * configuration is a different fact: vehicle creation is already gated on one existing, so its
 * absence is an integrity fault US-03 asks to be *reported*. Swallowing the 404 here would turn a
 * broken account into an empty window and leave the operator guessing why nothing was proposed.
 */
export function useVehicleTypeBillingConfig(vehicleType: string) {
  return useQuery({
    queryKey: ['billing-configs', 'vehicle-types', vehicleType],
    queryFn: () => apiClient.get<BillingConfigResponse>(`/v1/billing-configs/vehicle-types/${vehicleType}`),
    enabled: !!vehicleType,
    staleTime: 10 * 60 * 1000
  });
}

export function useUpsertBillingConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ vehicleType, data }: { vehicleType: string; data: UpsertBillingConfigRequest }) =>
      apiClient.put<BillingConfigResponse>(`/v1/billing-configs/vehicle-types/${vehicleType}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-configs'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    }
  });
}

export function useCostEstimate() {
  return useMutation({
    mutationFn: (request: CostEstimateRequest) => apiClient.post<CostEstimateResponse>('/v1/billing-configs/estimate', request)
  });
}

/**
 * Query-style cost estimate for live pricing previews (CreateWO live summary): re-runs
 * whenever the request changes. Pass null to disable (e.g. no priced lines yet).
 */
export function useCostEstimateQuery(request: CostEstimateRequest | null) {
  return useQuery({
    queryKey: ['billing-configs', 'estimate', request],
    queryFn: () => apiClient.post<CostEstimateResponse>('/v1/billing-configs/estimate', request as CostEstimateRequest),
    enabled: request != null && request.items.length > 0,
    placeholderData: (prev) => prev,
    staleTime: 15_000
  });
}

export function useDeleteBillingConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vehicleType: string) => apiClient.delete<void>(`/v1/billing-configs/vehicle-types/${vehicleType}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-configs'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    }
  });
}

// ──────────────────────────────────────────────
// Operational Schedule hooks
// ──────────────────────────────────────────────

export function useOperationalSchedule() {
  return useQuery({
    queryKey: ['operational-schedule'],
    queryFn: async () => {
      try {
        return await apiClient.get<OperationalScheduleResponse>('/v1/operational-schedule');
      } catch (e: any) {
        if (e?.status === 404) return null;
        throw e;
      }
    },
    retry: (failureCount, error: any) => {
      if (error?.status === 404) return false;
      return failureCount < 3;
    }
  });
}

export function useUpdateOperationalSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateOperationalScheduleRequest) => apiClient.put<void>('/v1/operational-schedule', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operational-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    }
  });
}

// ──────────────────────────────────────────────
// Pre-departure check (PM_A-driven, CHECKLIST-01: recorded at pre-rent check-in, advisory —
// does not gate departure)
// ──────────────────────────────────────────────

/**
 * Resolves the cycle config for a vehicle (per-vehicle override → account-default fallback).
 * A 404 (`ResourceNotFoundException` — the account never configured this cycle for the vehicle's
 * type) resolves to `null` rather than an error state: "no checklist configured" is an expected,
 * non-exceptional outcome for callers like the pre-rent check-in panel, which must keep working
 * for accounts that haven't touched `maintenance-config` yet.
 */
export function useResolvedMaintenanceCycleConfig(vehicleId: string, cycleCode: MaintenanceCycleCode = 'PM_A') {
  return useQuery({
    queryKey: ['maintenance', 'cycle-configs', 'resolved', vehicleId, cycleCode],
    queryFn: async () => {
      const params = new URLSearchParams({ vehicleId, cycleCode });
      try {
        return await apiClient.get<MaintenanceCycleConfigDTO>(`/maintenance/cycle-configs/resolved?${params}`);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    enabled: !!vehicleId
  });
}

// `ApiDTO` serializes with `@JsonInclude(NON_NULL)` (`ApiDTO.java`), so `findForItem`'s "no check
// yet" response omits the `data` key entirely rather than sending `data: null` — the unwrapped
// value is `undefined`, which React Query rejects ("Query data cannot be undefined"). Coerce to
// `null` so "not yet submitted" is a normal, cacheable result instead of a query error.
export function useWorkOrderItemPreDepartureCheck(workOrderId: string, itemId: string) {
  return useQuery({
    queryKey: ['work-orders', workOrderId, 'items', itemId, 'pre-departure-check'],
    queryFn: async () =>
      (await apiClient.get<PreDepartureCheckDTO | null>(`/work-orders/${workOrderId}/items/${itemId}/pre-departure-check`)) ?? null,
    enabled: !!workOrderId && !!itemId
  });
}

/**
 * Fetches the pre-departure check (or null) for every item in a work order in parallel.
 * Returns a flat `{ itemId, check, isLoading }[]` keyed in the same order as `itemIds`.
 */
export function useWorkOrderItemsPreDepartureChecks(workOrderId: string, itemIds: string[], enabled = true) {
  const results = useQueries({
    queries: itemIds.map((itemId) => ({
      queryKey: ['work-orders', workOrderId, 'items', itemId, 'pre-departure-check'],
      queryFn: async () =>
        (await apiClient.get<PreDepartureCheckDTO | null>(`/work-orders/${workOrderId}/items/${itemId}/pre-departure-check`)) ?? null,
      enabled: enabled && !!workOrderId && !!itemId
    }))
  });
  return itemIds.map((itemId, idx) => ({
    itemId,
    check: results[idx]?.data ?? null,
    isLoading: results[idx]?.isLoading ?? false
  }));
}

export function useSubmitPreDepartureCheck(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SubmitPreDepartureCheckRequest) =>
      apiClient.post<PreDepartureCheckDTO>(`/work-orders/${workOrderId}/items/${itemId}/pre-departure-check`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId, 'items', itemId, 'pre-departure-check'] });
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    }
  });
}

// ──────────────────────────────────────────────
// Work-order files (signed completion reports, supporting docs)
// ──────────────────────────────────────────────

export function useWorkOrderFiles(workOrderId: string, purpose?: WorkOrderFilePurpose) {
  const qs = purpose ? `?purpose=${purpose}` : '';
  return useQuery({
    queryKey: ['work-orders', workOrderId, 'files', purpose ?? 'all'],
    queryFn: () => apiClient.get<WorkOrderFileResponse[]>(`/work-orders/${workOrderId}/files${qs}`),
    enabled: !!workOrderId
  });
}

/**
 * VOICE-01 (VEK-181) — `workOrderId` on the variables overrides the hook's: the create page's recorder
 * learns the order's id (from `ensureDraftId`) in the same tick it uploads, before the render that
 * would hand this hook the id.
 */
export function useUploadWorkOrderFile(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      purpose,
      workOrderId: target = workOrderId
    }: {
      file: File;
      purpose: WorkOrderFilePurpose;
      workOrderId?: string;
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('purpose', purpose);
      return apiClient.upload<WorkOrderFileResponse>(`/work-orders/${target}/files`, formData);
    },
    onSuccess: (_data, { workOrderId: target = workOrderId }) => {
      queryClient.invalidateQueries({ queryKey: ['work-orders', target, 'files'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', target] });
    }
  });
}

export function useDeleteWorkOrderFile(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileId: string) => apiClient.delete<void>(`/work-orders/${workOrderId}/files/${fileId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId, 'files'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId] });
    }
  });
}

// VOICE-03 — recognizing the order's voice message. The browser never reaches the voice server; the
// API proxies both calls. Polling is paced by the caller (`VoiceMessageRecorder`), not by a query.
export function useVoiceTranscriptionConfig() {
  return useQuery({
    queryKey: ['voice-transcription', 'config'],
    queryFn: () => apiClient.get<VoiceTranscriptionConfigResponse>('/voice-transcription/config'),
    staleTime: Infinity
  });
}

export function useStartVoiceTranscription(workOrderId: string) {
  return useMutation({
    mutationFn: () => apiClient.post<VoiceTranscriptionJobResponse>(`/work-orders/${workOrderId}/voice-message/transcriptions`, {})
  });
}

export function fetchVoiceTranscription(workOrderId: string, jobId: string) {
  return apiClient.get<VoiceTranscriptionJobResponse>(`/work-orders/${workOrderId}/voice-message/transcriptions/${jobId}`);
}

// ──────────────────────────────────────────────
// IoT sensors & telemetry
// ──────────────────────────────────────────────

export function useVehicleActiveSensors(vehicleId: string, enabled = true) {
  return useQuery({
    queryKey: ['vehicles', vehicleId, 'sensors'],
    queryFn: () => apiClient.get<SensorAssignmentResponse[]>(`/vehicles/${vehicleId}/sensors`),
    enabled: !!vehicleId && enabled
  });
}

export function useVehicleLatestTelemetry(vehicleId: string, enabled = true) {
  return useQuery({
    queryKey: ['vehicles', vehicleId, 'telemetry', 'latest'],
    queryFn: () => apiClient.get<SensorReadingResponse[]>(`/vehicles/${vehicleId}/telemetry/latest`),
    enabled: !!vehicleId && enabled,
    // Telemetry refreshes on its own cadence — re-query every minute while the page is open
    // so "live · 2m ago" stays accurate without a manual refresh.
    refetchInterval: 60_000
  });
}

/**
 * Historical telemetry for the Monitoring section on the vehicle detail page.
 * `from` / `to` must be ISO-8601 strings. The API caps the window at 31 days and the
 * result set at 5000 rows — the caller is expected to validate the window before passing
 * `enabled = true`.
 */
export function useVehicleTelemetryHistory(vehicleId: string, from: string, to: string, enabled = true) {
  const qs = new URLSearchParams();
  qs.set('from', from);
  qs.set('to', to);
  qs.set('limit', '5000');
  return useQuery({
    queryKey: ['vehicles', vehicleId, 'telemetry', 'history', from, to],
    queryFn: () => apiClient.get<SensorReadingResponse[]>(`/vehicles/${vehicleId}/telemetry?${qs.toString()}`),
    enabled: !!vehicleId && !!from && !!to && enabled
  });
}

export function useAvailableSensors(params?: { sensorType?: SensorType; search?: string }) {
  const qs = new URLSearchParams();
  qs.set('assigned', 'false');
  qs.set('status', 'IN_STOCK');
  qs.set('size', '100');
  if (params?.sensorType) qs.set('sensorType', params.sensorType);
  if (params?.search) qs.set('search', params.search);
  return useQuery({
    queryKey: ['sensors', 'available', params?.sensorType ?? null, params?.search ?? null],
    queryFn: () => apiClient.get<PageResponse<SensorResponse>>(`/sensors?${qs.toString()}`)
  });
}

export interface UseSensorsParams {
  page?: number;
  size?: number;
  status?: SensorStatus;
  sensorType?: SensorType;
  assigned?: boolean;
  search?: string;
}

export function useSensors(params: UseSensorsParams = {}) {
  const { page = 0, size = 20, status, sensorType, assigned, search } = params;
  const qs = new URLSearchParams();
  qs.set('page', String(page));
  qs.set('size', String(size));
  if (status) qs.set('status', status);
  if (sensorType) qs.set('sensorType', sensorType);
  if (assigned != null) qs.set('assigned', String(assigned));
  if (search) qs.set('search', search);
  return useQuery({
    queryKey: ['sensors', 'list', page, size, status ?? null, sensorType ?? null, assigned ?? null, search ?? null],
    queryFn: () => apiClient.get<PageResponse<SensorResponse>>(`/sensors?${qs.toString()}`),
    refetchInterval: 60_000
  });
}

export function useCreateSensor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSensorRequest) => apiClient.post<SensorResponse>('/sensors', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sensors'] });
    }
  });
}

export function useAssignSensor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sensorId, payload }: { sensorId: string; payload: AssignSensorRequest }) =>
      apiClient.post<SensorAssignmentResponse>(`/sensors/${sensorId}/assignments`, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', variables.payload.vehicleId, 'sensors'] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', variables.payload.vehicleId, 'telemetry', 'latest'] });
      queryClient.invalidateQueries({ queryKey: ['sensors', 'available'] });
      queryClient.invalidateQueries({ queryKey: ['sensors', variables.sensorId] });
    }
  });
}

export function useUnassignSensor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sensorId }: { sensorId: string; vehicleId: string }) =>
      apiClient.delete<void>(`/sensors/${sensorId}/assignments/current`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', variables.vehicleId, 'sensors'] });
      queryClient.invalidateQueries({ queryKey: ['vehicles', variables.vehicleId, 'telemetry', 'latest'] });
      queryClient.invalidateQueries({ queryKey: ['sensors', 'available'] });
      queryClient.invalidateQueries({ queryKey: ['sensors', variables.sensorId] });
    }
  });
}

// ──────────────────────────────────────────────
// Work Order Management v2 (docs/features/work-order-management-v2)
//
// One hook per contracts/openapi.yaml operationId. Query keys are namespaced
// under 'work-orders-v2' / 'work-order-items-v2' / 'conflict-alerts-v2' so
// they never collide with the legacy `useWorkOrders` family above, which
// keeps using the pre-v2 endpoints and types. Hook names are suffixed `V2`
// to match the `V2`-suffixed types (types.ts).
// ──────────────────────────────────────────────

export interface ListWorkOrdersV2Filters {
  status?: WorkOrderStatusV2[];
  workOrderType?: WorkOrderTypeV2;
  blockerType?: BlockerType;
  customerId?: string;
  includeArchived?: boolean;
  assignedToMe?: boolean;
  /** PORTAL-01 (VEK-185) — only orders that began with staff, or only those from the portal. */
  origin?: WorkOrderOrigin;
}

// listWorkOrders
export function useWorkOrdersV2(page = 0, size = 20, filters?: ListWorkOrdersV2Filters) {
  const statusKey = filters?.status ? [...filters.status].sort().join(',') : undefined;
  return useQuery({
    queryKey: [
      'work-orders-v2',
      page,
      size,
      statusKey,
      filters?.workOrderType,
      filters?.blockerType,
      filters?.customerId,
      filters?.includeArchived,
      filters?.assignedToMe,
      filters?.origin
    ],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      filters?.status?.forEach((s) => params.append('status', s));
      if (filters?.workOrderType) params.append('workOrderType', filters.workOrderType);
      if (filters?.blockerType) params.append('blockerType', filters.blockerType);
      if (filters?.customerId) params.append('customerId', filters.customerId);
      if (filters?.includeArchived) params.append('includeArchived', 'true');
      if (filters?.assignedToMe) params.append('assignedToMe', 'true');
      if (filters?.origin) params.append('origin', filters.origin);
      return apiClient.get<PageResponse<WorkOrderSummaryV2>>(`/work-orders?${params}`);
    }
  });
}

// getWorkOrder
export function useWorkOrderV2(workOrderId: string, enabled = true) {
  return useQuery({
    queryKey: ['work-orders-v2', workOrderId],
    queryFn: () => apiClient.get<WorkOrderDetailV2>(`/work-orders/${workOrderId}`),
    enabled: enabled && !!workOrderId
  });
}

// createWorkOrder
export function useCreateWorkOrderV2() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: WorkOrderCreateRequest) => apiClient.post<WorkOrderDetailV2>('/work-orders', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2'] });
    }
  });
}

// updateWorkOrder
export function useUpdateWorkOrderV2(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: WorkOrderUpdateRequest) => apiClient.put<WorkOrderDetailV2>(`/work-orders/${workOrderId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
    }
  });
}

// archiveWorkOrder — the pre-v2 PATCH endpoint is still the only archive surface, but the
// v2 list is the caller, so invalidation targets the v2 query family.
export function useArchiveWorkOrderV2() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (workOrderId: string) => apiClient.patch<WorkOrderResponse>(`/work-orders/${workOrderId}/archive`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2'] });
    }
  });
}

// addWorkOrderItem
export function useAddWorkOrderItemV2(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // Mirrors the backend: addWorkOrderItem returns the full updated order, not the created line.
    mutationFn: (data: WorkOrderItemDraftRequest) => apiClient.post<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items`, data),
    // The response already IS the query's shape, so writing it straight into the cache skips the
    // extra GET a plain invalidate would trigger — the new line shows up on this response alone.
    onSuccess: (response) => {
      queryClient.setQueryData(['work-orders-v2', workOrderId], response);
    }
  });
}

// updateWorkOrderItem
export function useUpdateWorkOrderItemV2(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // The server answers with the whole order, not the line it changed
    // (`WorkOrderController.updateWorkOrderItem` → `ApiDTO<WorkOrderDTO>`). This said
    // `WorkOrderItemV2` until phase 10 plan 07, which is a type that would have handed the first
    // caller to read the response an order where it expected an entry.
    mutationFn: (data: WorkOrderItemDraftRequest) => apiClient.put<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items/${itemId}`, data),
    // Same fix as useAddWorkOrderItemV2: the response already is the order, so write it straight
    // into the cache instead of paying for an extra GET via invalidateQueries.
    onSuccess: (response) => {
      queryClient.setQueryData(['work-orders-v2', workOrderId], response);
    }
  });
}

/**
 * US-03 AC 6 (gap S12) — patchWorkOrderItemControls: send ONE of an entry's own controls.
 *
 * Curried per entry like the mutation above, and deliberately not interchangeable with it: the PUT
 * is a full replace, so a body carrying only the notes clears the vehicle, the window and the
 * discount. Here an omitted field is left unchanged, and clearing is explicit — `0` for the
 * discount, `""` for the notes.
 *
 * The server answers with the whole order, not the line, so one invalidation of the order key is
 * both necessary and sufficient.
 */
export function usePatchWorkOrderItemControls(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PatchWorkOrderItemControlsRequest) =>
      apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items/${itemId}/controls`, data),
    // Same fix as useAddWorkOrderItemV2: the response already is the order, so write it straight
    // into the cache instead of paying for an extra GET via invalidateQueries.
    onSuccess: (response) => {
      queryClient.setQueryData(['work-orders-v2', workOrderId], response);
    }
  });
}

// deleteWorkOrderItem
export function useDeleteWorkOrderItemV2(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // Deleting an entry releases its hold in the SAME server action
    // (`WorkOrderServiceImpl.deleteWorkOrderItem` cancels the reservations before removing the
    // row), so no caller should put a release call in front of this one.
    mutationFn: (itemId: string) => apiClient.delete<void>(`/work-orders/${workOrderId}/items/${itemId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
    }
  });
}

/**
 * US-03 AC 4 (gap S17) — hand the machine back and KEEP the entry.
 *
 * The server cancels the entry's reservations and leaves the row (`releaseItemHold`), so the row
 * stays on screen and only its hold chip goes: `holdActive` is computed at read from whether a
 * live reservation exists, so one invalidation of the order key is what makes the chip disappear.
 *
 * Deliberately un-confirmed at every call site. AC 4 specifies one click and gives the reason: the
 * pick itself is not gated behind a confirmation precisely because the release is cheap to undo.
 */
export function useReleaseWorkOrderItemHold(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => apiClient.delete<void>(`/work-orders/${workOrderId}/items/${itemId}/reservation`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
    }
  });
}

// submitWorkOrder
export function useSubmitWorkOrderV2(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // Body carries the payment schedule + availability-conflict escape hatch; the rest of
    // the legacy finalize DTO is ignored by the server (values come from the persisted draft).
    mutationFn: (body: WorkOrderSubmitRequest = {}) => apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/submit`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
    }
  });
}

// cancelWorkOrder
export function useCancelWorkOrderV2(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CancelRequest) => apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/cancel`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
      // PAY-03: a close from PAYMENT_VERIFICATION or INTERRUPTED may have written payments off.
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId, 'payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    }
  });
}

// assignFleetManager
export function useAssignFleetManagerV2(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FleetManagerAssignRequest) => apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/fleet-manager`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
    }
  });
}

// getPaymentGates
export function usePaymentGatesV2(workOrderId: string, enabled = true) {
  return useQuery({
    queryKey: ['work-orders-v2', workOrderId, 'payment-gates'],
    queryFn: () => apiClient.get<PaymentGate[]>(`/work-orders/${workOrderId}/payment-gates`),
    enabled: enabled && !!workOrderId
  });
}

// reviewUpfrontGate
export function useReviewUpfrontGateV2(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: GateReviewRequest) =>
      apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/payments/upfront-review`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId, 'payment-gates'] });
    }
  });
}

// reviewFinalGate is GONE (PAY-03): the FINAL gate and its route are deleted.

/**
 * PAY-03 — the office collection: whoever received a scheduled payment records that they did.
 * `PENDING` → `COLLECTED`, or straight to `CONFIRMED` when the caller is a money role.
 */
export function useRecordPaymentCollectionV2(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ paymentId, data }: { paymentId: string; data?: RecordPaymentCollectionRequest }) =>
      apiClient.post<PaymentResponse>(`/work-orders/${workOrderId}/payments/${paymentId}/collection`, data ?? {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId, 'payment-gates'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId, 'payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    }
  });
}

/**
 * PAY-03 — close an order in `PAYMENT_VERIFICATION` as `COMPLETED` once every vehicle is home,
 * writing the unconfirmed payments off only with `acknowledgeUnsettledPayments`.
 */
export function useCompleteWorkOrderV2(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CloseWorkOrderRequest) => apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/complete`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId, 'payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    }
  });
}

// recordFieldPaymentCollection
export function useRecordFieldPaymentCollectionV2(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PaymentCollectionRequest) =>
      apiClient.post<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items/${itemId}/payment-collection`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
    }
  });
}

// declareCashNotPaid (PAY-07)
export function useDeclareCashNotPaidV2(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CashNotPaidRequest) =>
      apiClient.post<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items/${itemId}/cash-not-paid`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
    }
  });
}

// assignOperator
export function useAssignOperatorV2(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: OperatorAssignmentRequest) =>
      apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items/${itemId}/operator-assignment`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-order-items-v2'] });
    }
  });
}

/**
 * VEK-124 (US-08 AC 4) — the operator picker's list for one entry or a bulk selection: each fleet operator
 * free, tight or busy against the selected windows, with the last rejection of each entry. Never cached across
 * openings — someone else's assignment a minute ago changes who is free.
 */
export function useOperatorCandidates(workOrderId: string, itemIds: string[], enabled = true) {
  return useQuery({
    queryKey: ['work-orders-v2', workOrderId, 'operator-candidates', itemIds],
    queryFn: async () => {
      const query = itemIds.map((id) => `itemIds=${encodeURIComponent(id)}`).join('&');
      return apiClient.get<OperatorCandidatesResponse>(`/work-orders/${workOrderId}/items/operator-candidates?${query}`);
    },
    enabled: enabled && !!workOrderId && itemIds.length > 0,
    staleTime: 0,
    gcTime: 0
  });
}

// bulkAssignOperator
export function useBulkAssignOperatorV2(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: BulkOperatorAssignmentRequest) =>
      apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items/bulk-operator-assignment`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-order-items-v2'] });
    }
  });
}

// confirmOperatorAssignment
export function useConfirmOperatorAssignmentV2(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: OperatorConfirmationRequest) =>
      apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items/${itemId}/operator-confirmation`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-order-items-v2'] });
    }
  });
}

// startPreRentCheck
// UI-11 (operator-steps-rebuild) — OPERATOR_CONFIRMED → PRE_RENT_CHECK_IN, guarded server-side by
// the 24-hour preparation window. Empty body: the command carries nothing but the transition.
export function useStartPreRentCheckV2(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items/${itemId}/pre-rent-start`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-order-items-v2'] });
    }
  });
}

// preRentCheckIn
export function usePreRentCheckInV2(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PreRentCheckInRequest) =>
      apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items/${itemId}/pre-rent-check-in`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-order-items-v2'] });
      // VEK-65 — the reading moved the vehicle's fuel level and wrote its fuel log.
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['vehicle-fuel-events'] });
    }
  });
}

// confirmDeparture
export function useConfirmDepartureV2(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data?: TransitionNoteRequest) =>
      apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items/${itemId}/departure`, data ?? {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-order-items-v2'] });
    }
  });
}

// startRent
export function useStartRentV2(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data?: RentStartRequest) =>
      apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items/${itemId}/rent-start`, data ?? {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-order-items-v2'] });
    }
  });
}

// endRent
export function useEndRentV2(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data?: TransitionNoteRequest) =>
      apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items/${itemId}/rent-end`, data ?? {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-order-items-v2'] });
    }
  });
}

// startReturn
export function useStartReturnV2(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data?: ReturnStartRequest) =>
      apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items/${itemId}/return-start`, data ?? {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-order-items-v2'] });
    }
  });
}

// returnCheckIn
export function useReturnCheckInV2(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ReturnCheckInRequest) =>
      apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items/${itemId}/return-check-in`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-order-items-v2'] });
      // VEK-65 — the reading moved the vehicle's fuel level and wrote its fuel log.
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['vehicle-fuel-events'] });
    }
  });
}

// cancelWorkOrderItem
export function useCancelWorkOrderItemV2(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ItemCancelRequest) => apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items/${itemId}/cancel`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-order-items-v2'] });
    }
  });
}

// replaceVehicle
export function useReplaceVehicleV2(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ReplaceVehicleRequest) =>
      apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items/${itemId}/replace-vehicle`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-order-items-v2'] });
    }
  });
}

// RENT-01 / RENT-02 — the four extension commands. Every one invalidates the order, its entries, the
// payment schedule (an approval inserts a PENDING row) and the conflict alerts (an office approval
// that collides raises one).
function invalidateAfterExtension(queryClient: ReturnType<typeof useQueryClient>, workOrderId: string) {
  queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
  queryClient.invalidateQueries({ queryKey: ['work-order-items-v2'] });
  queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId, 'payments'] });
  queryClient.invalidateQueries({ queryKey: ['conflict-alerts-v2'] });
}

// extendRental — the office extends directly: a request approved in the same act
export function useExtendRentalV2(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DirectExtensionRequest) =>
      apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items/${itemId}/extension`, data),
    onSuccess: () => invalidateAfterExtension(queryClient, workOrderId)
  });
}

// requestExtension — the operator's ask
export function useRequestExtensionV2(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RentalExtensionRequest) =>
      apiClient.post<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items/${itemId}/extensions`, data),
    onSuccess: () => invalidateAfterExtension(queryClient, workOrderId)
  });
}

// approveExtension — the office's yes
export function useApproveExtensionV2(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ extensionId, ...data }: RentalExtensionDecision & { extensionId: string }) =>
      apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items/${itemId}/extensions/${extensionId}/approve`, data),
    onSuccess: () => invalidateAfterExtension(queryClient, workOrderId)
  });
}

// rejectExtension — the office's no
export function useRejectExtensionV2(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ extensionId, ...data }: RentalExtensionRejection & { extensionId: string }) =>
      apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items/${itemId}/extensions/${extensionId}/reject`, data),
    onSuccess: () => invalidateAfterExtension(queryClient, workOrderId)
  });
}

// resolveDamageReview
export function useResolveDamageReviewV2(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DamageReviewRequest) =>
      apiClient.patch<WorkOrderDetailV2>(`/work-orders/${workOrderId}/items/${itemId}/damage-review`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-order-items-v2'] });
    }
  });
}

// TASK-01 — the order-level task queue. Which queue comes back is decided from the caller's
// primary role on the server; the client sends nothing but the page.
export function useMyTasks(page = 0, size = 20, enabled = true) {
  return useQuery({
    queryKey: ['work-orders-my-tasks', page, size],
    enabled,
    queryFn: () => apiClient.get<PageResponse<WorkOrderTaskV2>>(`/work-orders/my-tasks?page=${page}&size=${size}`)
  });
}

export interface ListWorkOrderItemsV2Filters {
  status?: WorkOrderItemStatusV2[];
  assignedToMe?: boolean;
  workOrderId?: string;
  /** TASK-02 — both or neither (ISO instants): entries whose rent window overlaps [from, to). */
  from?: string;
  to?: string;
}

// listWorkOrderItems
export function useWorkOrderItemsV2(page = 0, size = 20, filters?: ListWorkOrderItemsV2Filters) {
  const statusKey = filters?.status ? [...filters.status].sort().join(',') : undefined;
  return useQuery({
    queryKey: ['work-order-items-v2', page, size, statusKey, filters?.assignedToMe, filters?.workOrderId, filters?.from, filters?.to],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      filters?.status?.forEach((s) => params.append('status', s));
      if (filters?.assignedToMe) params.append('assignedToMe', 'true');
      if (filters?.workOrderId) params.append('workOrderId', filters.workOrderId);
      if (filters?.from && filters?.to) {
        params.append('from', filters.from);
        params.append('to', filters.to);
      }
      return apiClient.get<PageResponse<WorkOrderItemV2>>(`/work-order-items?${params}`);
    }
  });
}

// getWorkOrderHistory
/**
 * EVENT-02 — appends the filter spine to a query string.
 *
 * An absent or empty value is omitted rather than sent blank: the server reads "no opinion" from a
 * missing parameter, and an empty one would narrow to nothing.
 */
function appendEventFilters(params: URLSearchParams, filters?: EventFilterParams) {
  if (!filters) return;
  const lists: [string, (string | undefined)[] | undefined][] = [
    ['category', filters.category],
    ['origin', filters.origin],
    ['severity', filters.severity],
    ['actorRole', filters.actorRole],
    ['statusAfter', filters.statusAfter]
  ];
  lists.forEach(([key, values]) => (values ?? []).forEach((v) => v && params.append(key, v)));
  if (filters.from) params.append('from', filters.from);
  if (filters.to) params.append('to', filters.to);
  if (filters.correlationId) params.append('correlationId', filters.correlationId);
}

export function useWorkOrderHistoryV2(
  workOrderId: string,
  page = 0,
  size = 20,
  workOrderItemId?: string,
  enabled = true,
  filters?: EventFilterParams,
  /** UI-12 — `'desc'` asks for the newest rows first; the default keeps the server's oldest-first order. */
  order: 'asc' | 'desc' = 'asc'
) {
  return useQuery({
    queryKey: ['work-orders-v2', workOrderId, 'history', page, size, workOrderItemId, filters, order],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      if (order === 'desc') params.append('sort', 'changedAt,desc');
      if (workOrderItemId) params.append('workOrderItemId', workOrderItemId);
      appendEventFilters(params, filters);
      return apiClient.get<PageResponse<StatusHistoryEntry>>(`/work-orders/${workOrderId}/history?${params}`);
    },
    enabled: enabled && !!workOrderId
  });
}

/**
 * UI-12 — the URL a stored file is read through (`GET /v1/files/url?key=`). A private key answers a
 * presigned URL that lives 15 minutes on the server (`StorageServiceImpl.PRIVATE_URL_TTL`); the
 * 10-minute `staleTime` keeps a cached answer inside that window, so a tile never draws a link that
 * has already expired. Deduplicated per key across every tile on the page.
 */
export function useFileUrl(key: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ['file-url', key],
    queryFn: () => apiClient.get<string>(`/v1/files/url?key=${encodeURIComponent(key as string)}`),
    enabled: enabled && !!key,
    staleTime: 10 * 60 * 1000,
    retry: false
  });
}

/** EVENT-03 — a machine's timeline, newest first. */
export function useVehicleEvents(vehicleId: string, page = 0, size = 20, filters?: EventFilterParams, enabled = true) {
  return useQuery({
    queryKey: ['vehicle-events', vehicleId, page, size, filters],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      appendEventFilters(params, filters);
      return apiClient.get<PageResponse<VehicleEvent>>(`/vehicles/${vehicleId}/events?${params}`);
    },
    enabled: enabled && !!vehicleId
  });
}

/** VEK-65 — a vehicle's fuel log, newest first. */
export function useVehicleFuelEvents(vehicleId: string, page = 0, size = 20, enabled = true) {
  return useQuery({
    queryKey: ['vehicle-fuel-events', vehicleId, page, size],
    queryFn: () => apiClient.get<PageResponse<VehicleFuelEvent>>(`/vehicles/${vehicleId}/fuel/events?page=${page}&size=${size}`),
    enabled: enabled && !!vehicleId
  });
}

/**
 * VEK-65 — refuel a vehicle, outside any work order. Invalidates the vehicle, its fuel log and history,
 * and every open work order (the operator page reads the vehicle's level off the entry).
 */
export function useRefuelVehicle(vehicleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RefuelVehicleRequest) => apiClient.post<VehicleResponse>(`/vehicles/${vehicleId}/fuel/refills`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['vehicle-fuel-events', vehicleId] });
      queryClient.invalidateQueries({ queryKey: ['vehicle-events', vehicleId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2'] });
    }
  });
}

/**
 * EVENT-04 — file a field remark against an entry.
 *
 * Invalidates the order's history so the remark appears on the timeline without a reload; the row is
 * written synchronously server-side, so it is there by the time this resolves.
 */
export function useFileRemark(workOrderId: string, itemId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: FileRemarkRequest) =>
      apiClient.post<StatusHistoryEntry>(`/work-orders/${workOrderId}/items/${itemId}/remarks`, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId, 'history'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2', workOrderId] });
    }
  });
}

export interface ListConflictAlertsV2Filters {
  status?: ConflictAlertStatus;
  alertType?: ConflictAlertType;
}

// listConflictAlerts
export function useConflictAlertsV2(page = 0, size = 20, filters?: ListConflictAlertsV2Filters) {
  return useQuery({
    queryKey: ['conflict-alerts-v2', page, size, filters?.status, filters?.alertType],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), size: String(size) });
      if (filters?.status) params.append('status', filters.status);
      if (filters?.alertType) params.append('alertType', filters.alertType);
      return apiClient.get<PageResponse<ConflictAlert>>(`/conflict-alerts?${params}`);
    }
  });
}

// resolveConflictAlert
export function useResolveConflictAlertV2() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ alertId, ...data }: { alertId: string } & ConflictResolutionRequest) =>
      apiClient.patch<ConflictAlert>(`/conflict-alerts/${alertId}/resolve`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conflict-alerts-v2'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2'] });
      queryClient.invalidateQueries({ queryKey: ['work-order-items-v2'] });
    }
  });
}

// PAY-11 — the account-wide payments table. Filters, sort and page all travel to the server; the
// export takes the same filters and sort plus the visible columns, unpaged.
function paymentLedgerParams(filters: PaymentLedgerFilters, sort: PaymentLedgerSort | null): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.customer?.trim()) params.set('customer', filters.customer.trim());
  filters.types?.forEach((type) => params.append('types', type));
  filters.statuses?.forEach((status) => params.append('statuses', status));
  if (filters.amountMin != null) params.set('amountMin', String(filters.amountMin));
  if (filters.amountMax != null) params.set('amountMax', String(filters.amountMax));
  if (filters.dueFrom) params.set('dueFrom', filters.dueFrom);
  if (filters.dueTo) params.set('dueTo', filters.dueTo);
  if (filters.phone?.trim()) params.set('phone', filters.phone.trim());
  if (sort) params.set('sort', `${sort.key},${sort.direction}`);
  return params;
}

export function usePaymentLedger(filters: PaymentLedgerFilters, sort: PaymentLedgerSort | null, page = 0, size = 20, enabled = true) {
  return useQuery({
    queryKey: ['payment-ledger', filters, sort, page, size],
    enabled,
    placeholderData: (prev) => prev,
    queryFn: () => {
      const params = paymentLedgerParams(filters, sort);
      params.set('page', String(page));
      params.set('size', String(size));
      return apiClient.get<PageResponse<PaymentLedgerRow>>(`/v1/payments?${params}`);
    }
  });
}

export function useExportPayments() {
  return useMutation({
    mutationFn: ({
      filters,
      sort,
      columns
    }: {
      filters: PaymentLedgerFilters;
      sort: PaymentLedgerSort | null;
      columns: PaymentLedgerColumnKey[];
    }) => {
      const params = paymentLedgerParams(filters, sort);
      columns.forEach((column) => params.append('columns', column));
      return apiClient.get<PaymentExport>(`/v1/payments/export?${params}`);
    }
  });
}

export function useRemindPayment() {
  return useMutation({
    mutationFn: (paymentId: string) => apiClient.post<PaymentRemindResult>(`/v1/payments/${paymentId}/remind`, {})
  });
}

/** The per-payment confirm (PAY-03) from a table row, where the order varies per call; invalidates what the order page's hook does. */
export function useConfirmLedgerPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workOrderId, paymentId }: { workOrderId: string; paymentId: string }) =>
      apiClient.patch<PaymentResponse>(`/work-orders/${workOrderId}/payments/${paymentId}/confirm`, {}),
    onSuccess: (_data, { workOrderId }) => {
      queryClient.invalidateQueries({ queryKey: ['payment-ledger'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-my-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders-v2'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    }
  });
}

// ── SUPPORT-01 (VEK-161) — support requests ───────────────────────────────────

/** The account's support requests, newest first; every seat. */
export function useSupportRequests(page = 0, size = 20) {
  return useQuery({
    queryKey: ['support-requests', page, size],
    queryFn: () =>
      apiClient.get<PageResponse<SupportRequestResponse>>(
        `/support-requests?${new URLSearchParams({ page: String(page), size: String(size) })}`
      ),
    placeholderData: (prev) => prev
  });
}

/** One request with its files, each carrying a signed URL. */
export function useSupportRequest(id: string | null) {
  return useQuery({
    queryKey: ['support-requests', 'detail', id],
    queryFn: () => apiClient.get<SupportRequestResponse>(`/support-requests/${id}`),
    enabled: !!id
  });
}

export function useCreateSupportRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSupportRequestRequest) => apiClient.post<SupportRequestResponse>('/support-requests', data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['support-requests'] })
  });
}

/** The creator attaches one file while the request is CREATED; the dialog calls it once per picked file. */
export function useUploadSupportRequestFile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, file }: { requestId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiClient.upload<SupportRequestFileResponse>(`/support-requests/${requestId}/files`, formData);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['support-requests'] })
  });
}

// ──────────────────────────────────────────────
// Kharkiv address register — ADDR-06 (VEK-150)
// ──────────────────────────────────────────────

/**
 * Current Kharkiv streets matching a name part, current or old. Our own API answers from its copy of
 * the register, so asking per keystroke is fine — the OSM policy that keeps ordinary search on submit
 * does not apply. The caller debounces `query`.
 */
export function useKharkivStreets(query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: ['kharkiv-register', 'streets', q],
    queryFn: () => apiClient.get<KharkivStreetResponse[]>(`/v1/address-register/kharkiv/streets?${new URLSearchParams({ q, size: '15' })}`),
    enabled: q.length >= 2,
    retry: false,
    staleTime: 60 * 60 * 1000
  });
}

/** Houses of one current Kharkiv street. A failure means the register is unavailable — the editor says so. */
export function useKharkivHouses(streetId?: number) {
  return useQuery({
    queryKey: ['kharkiv-register', 'houses', streetId],
    queryFn: () => apiClient.get<KharkivHouseResponse[]>(`/v1/address-register/kharkiv/streets/${streetId}/houses`),
    enabled: streetId != null,
    retry: false,
    staleTime: 60 * 60 * 1000
  });
}
